import { shouldFilterUserMessages } from '../utils/user-messages';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import {
  EAttachmentType,
  EMessageDirection,
  EMessageStatus,
  EMessageType,
  toast,
  type IChatAttachment,
  type IChatMessage,
} from '@kognitos/lattice';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import noop from 'lodash/noop';

import {
  V1GenerationErrorKind,
  type Rainv1Value,
  type V1Event,
  type V1QuestionAnswer,
} from '@/generated/hooks/rain/models';
import type { V1Asset } from '@/generated/hooks/bishop/models';
import { useAuth } from '@/shared/auth';
import { isE2ETestMode } from '@/shared/auth/utils/e2e';
import { captureException, captureMessage, Sentry } from '@/modules/sentry';
import {
  detectFileType,
  mapFileTypeToAttachmentType,
} from '@/modules/file-renderer/utils/file-type-detection';
import {
  EStreamProtocol,
  ERetryStrategy,
  useStream,
  type IMessageMeta,
  type IStreamError,
} from '@/shared/streaming';
import { errorHandler } from '@/shared/utils/error-handler';

import {
  applyPlanEvent,
  createPlanStreamState,
  isLivePlanRetired,
  rollbackPlanEvents,
  selectStreamPlans,
} from '../utils/plan-stream';
import { RainStreamPlanCard } from '../components/RainTodoCard/RainTodoCard';

import {
  COMPACTION_ID_PREFIX,
  isRainScaffoldingTool,
  FOLLOW_UP_ID_PREFIX,
  INTERRUPT_ID_PREFIX,
  MAX_ITERATIONS_ID_PREFIX,
  SLEEP_ID_PREFIX,
  STREAM_RETRY_BASE_DELAY_MS,
  STREAM_RETRY_MAX_ATTEMPTS,
  STREAM_RETRY_MAX_DELAY_MS,
  SUB_AGENT_TASK_ID_PREFIX,
  TOOL_CALL_ID_PREFIX,
  TRANSFER_MARKER_ID_PREFIX,
} from '../constants';
import { parseUploadReminder } from '../utils/attachment-reminder';
import {
  getEventTaskScope,
  ROOT_TASK_SCOPE,
  parseRecordedAtMs,
} from '../utils/events';
import { buildRainEventLogMeta } from '../utils/stream-logging';
import {
  MAX_ITERATIONS_CONTINUE_MESSAGE,
  MAX_ITERATIONS_STOP_MESSAGE,
  isMaxIterationsFailure,
  parseMaxIterationsCount,
} from '../utils/max-iterations';
import { skipConnectionMessage } from '../utils/request-connection';
import {
  generationReducer,
  INITIAL_GENERATION_STATE,
  selectIsGenerating,
} from '../utils/generation-state';
import { buildRainUserMessage } from '../utils/message';
import {
  collectRolledBackMessageIds,
  dropMessagesById,
  parseDeletedEventRange,
  pruneStateByMessageId,
  recordMessageOrigin,
  type TMessageOriginIndex,
} from '../utils/rollback';
import {
  buildCustomMessage,
  buildOutgoingContent,
  buildStoppedMessage,
  createRandomMessageId,
  sanitizeUserActionForDisplay,
  upsertById,
} from '../utils/message-helpers';
import { useRainPendingFirstMessageStore } from '../stores/pending-first-message.store';
import {
  appendDeltaIntoSink,
  applyCompactComplete,
  applyCompactFailed,
  applyCompactStart,
  applyFollowUp,
  applyInterrupt,
  applyInterruptAnswer,
  applySleepCompleted,
  applySleepRequested,
  applyToolApprovalDenied,
  applyToolApprovalGranted,
  applyToolApprovalRequested,
  applyToolCallArgument,
  applyToolCallStart,
  applyToolResultToSink,
  closeFollowUpForSink,
  closeUnansweredInterruptsForSink,
  sealAssistantTextOnBoundary,
} from '../utils/event-handlers';
import { useRainMessageBuilders } from './useRainMessageBuilders';
import type {
  IAssistantAccumulator,
  IMessageSink,
  IRainToolActivity,
  IRainToolResolvers,
  TRainToolRenderer,
  IRainCompactionState,
  IRainFollowUpState,
  IRainInterruptState,
  IRainMaxIterationsState,
  IRainMessageDraft,
  IRainSendAttachment,
  IRainSleepState,
  IRainSubAgentTaskState,
  IRainToolCallState,
  IRainTransferState,
  IUseRainThreadMessagesParams,
  IUseRainThreadMessagesReturn,
} from './types';

export type {
  IUseRainThreadMessagesParams,
  IUseRainThreadMessagesReturn,
} from './types';

/** Shared empty map so an agent with no custom cards keeps a stable identity. */
const EMPTY_TOOL_RENDERERS: Readonly<Record<string, TRainToolRenderer>> = {};
const EMPTY_TOOL_ACTIVITIES: Readonly<Record<string, IRainToolActivity>> = {};

/**
 * Drops the streaming accumulators a task held while running. The final
 * nestedMessages (rendered cards, assistant text) stay on the task — those
 * are what the user reads. The Maps only existed to coalesce in-flight
 * deltas into a single upsert; once the task is done they're dead weight.
 */
const releaseTaskStreamingState = (task: IRainSubAgentTaskState): void => {
  task.outputAccum.clear();
  task.reasoningAccum.clear();
  task.toolCallIndexToId.clear();
};

export function useRainThreadMessages({
  rainThreadId,
  pendingFirstMessage,
  adapter,
  onAttachmentClick,
  onToolSucceeded,
  onVisibleToolResult,
}: IUseRainThreadMessagesParams): IUseRainThreadMessagesReturn {
  const queryClient = useQueryClient();

  // Scope-bound API surface. Destructure the stable functions out so the
  // hook's callbacks/effects depend on them directly (the adapter object itself
  // is rebuilt each render, but each function is useCallback-stable and the
  // changing query data is read as plain values). This keeps the SSE effect
  // from reconnecting on every render.
  const { agentConfig, agentId: rootAgentId } = adapter;
  const sendMessageToAdapter = adapter.sendMessage;
  const sendToolResultToAdapter = adapter.sendToolResult;
  const { streamUrl } = adapter;
  const { getActivePlanQueryKey } = adapter;
  const backlogData = adapter.events.data;
  const isFetchingBacklog = adapter.events.isFetching;
  const backlogErrorRaw = adapter.events.error;

  /*
   * Under `full` verbosity the profile's visible set is bypassed entirely: a
   * surface that exists to show what the agent did cannot be the one that
   * silently drops a call. A name absent from the profile is exactly the case
   * worth seeing there — it means the agent's tools have outgrown what this
   * repo knows about.
   *
   * A tool call still needs an id to be tracked, so the `name !== undefined`
   * floor stays: an event with no name has nothing to key a card on.
   */
  const isFullVerbosity = agentConfig.toolVerbosity === 'full';
  const isVisibleTool = useCallback(
    (name: string | undefined): boolean =>
      isFullVerbosity ? name !== undefined : agentConfig.isVisibleTool(name),
    [agentConfig, isFullVerbosity],
  );

  // A tool absent from the agent's profile renders nothing, which is right for
  // runtime scaffolding and wrong if the profile has drifted from the agent's
  // CR. Report each dropped name once per thread so the second case surfaces as
  // a warning rather than a missing card — skipping the scaffolding, which every
  // profile hides on purpose and which would otherwise bury the signal under
  // several events per thread.
  const reportedDroppedToolsRef = useRef<Set<string>>(new Set());
  const reportDroppedToolCall = useCallback(
    (name: string | undefined): void => {
      if (!name || isRainScaffoldingTool(name)) return;
      if (agentConfig.internalTools?.has(name)) return;
      if (reportedDroppedToolsRef.current.has(name)) return;
      reportedDroppedToolsRef.current.add(name);
      captureMessage(
        `Rain tool "${name}" is not in agent "${rootAgentId}"'s visible set — no card rendered`,
        {
          level: 'info',
          tags: { module: 'rain-chat', action: 'tool_call_dropped' },
          extra: { toolName: name, agentId: rootAgentId },
        },
      );
    },
    [agentConfig, rootAgentId],
  );

  // Client-side remote-tool fulfillment seam. Only the agents that self-fulfill
  // remote tools (the Builder) provide these; every other agent leaves them
  // undefined and the auto-fulfill path below is inert. Read through refs so
  // the SSE effect doesn't reconnect when the adapter object is rebuilt.
  const onRemoteToolCallRef = useRef(agentConfig.onRemoteToolCall);
  onRemoteToolCallRef.current = agentConfig.onRemoteToolCall;
  const resolvesToolCallRef = useRef(agentConfig.resolvesToolCall);
  resolvesToolCallRef.current = agentConfig.resolvesToolCall;

  const { t } = useTranslation('rain');
  const { getAccessTokenSilently } = useAuth();

  const [messages, setMessages] = useState<IChatMessage[]>([]);

  // The server keeps only the active plan, so the log is the only record.
  const planStreamRef = useRef(createPlanStreamState());
  const [planStreamVersion, setPlanStreamVersion] = useState(0);
  const planCardCacheRef = useRef(
    new Map<string, { lastEventId: number; message: IChatMessage }>(),
  );
  // null = root agent is responding (initial state or after a back-transfer).
  // Non-null = a transfer is active; the value is the target agent's id.
  const [activeAgentId, setActiveAgentId] = useState<string | null>(null);
  // The transfer that put us on `activeAgentId`, so a rollback can tell
  // whether the retired transfer is that one (a later transfer to the same
  // agent is not). Written synchronously wherever setActiveAgentId is called:
  // both transports dispatch a batch of events in one synchronous loop, so a
  // value mirrored during render would still be stale when the rollback for
  // the same batch runs.
  const activeTransferIdRef = useRef<string | null>(null);
  // Generation state lives in a single reducer driven by the event stream
  // (one dispatch per event in handleStreamEvent, plus a reset on stream
  // error). The derived `isGenerating` boolean replaces the previous
  // scattered `setIsGenerating(true/false)` calls — see
  // ../utils/generation-state.ts for the state machine.
  const [generationState, dispatchGeneration] = useReducer(
    generationReducer,
    INITIAL_GENERATION_STATE,
  );
  // True while one or more internal (non-UI-visible) tool calls are in flight
  // at root — artifact reads/writes, todo tracking, etc. These render no card,
  // so without this the chat looks idle while the agent is busy (e.g. writing a
  // lot of code). RainChat surfaces a generic working indicator while it's set.
  // See ENG-4067.
  const [isRunningInternalTool, setIsRunningInternalTool] = useState(false);

  const cancelledRef = useRef(false);
  const rootOutputAccumRef = useRef<Map<string, IAssistantAccumulator>>(
    new Map(),
  );
  const rootReasoningAccumRef = useRef<Map<string, IAssistantAccumulator>>(
    new Map(),
  );
  const rootToolCallsRef = useRef<Map<string, IRainToolCallState>>(new Map());
  const rootToolCallIndexToIdRef = useRef<Map<string, string>>(new Map());
  const rootInterruptsRef = useRef<Map<string, IRainInterruptState>>(new Map());
  const rootCompactionsRef = useRef<Map<string, IRainCompactionState>>(
    new Map(),
  );
  const rootSleepsRef = useRef<Map<string, IRainSleepState>>(new Map());
  const rootFollowUpsRef = useRef<Map<string, IRainFollowUpState>>(new Map());
  // Max-iterations question cards (MAN-3769), keyed by card id. Root-only: the
  // tool-loop cap is a root generation failure, so there's no per-sink variant.
  const rootMaxIterationsRef = useRef<Map<string, IRainMaxIterationsState>>(
    new Map(),
  );
  // Tracks the id of the currently-open follow-up card at root so successive
  // follow_up events within a single turn coalesce into one card. Reset on the
  // next user-initiated send so a new turn opens a fresh card.
  const rootCurrentFollowUpIdRef = useRef<string | null>(null);

  // Rendered message id -> the server event ids it was built from, so an
  // `events_deleted` rollback can find the bubbles a deleted turn produced.
  // See handleEventsDeleted.
  const messageOriginsRef = useRef<TMessageOriginIndex>(new Map());
  // The event being routed. upsertMessage is called deep inside handlers that
  // don't take the event, so this carries it to them. Cleared after each event
  // so an out-of-band upsert records no origin rather than a stale one.
  const currentEventIdRef = useRef<string | undefined>(undefined);

  const subAgentTasksRef = useRef<Map<string, IRainSubAgentTaskState>>(
    new Map(),
  );
  // Tracks every call_id seen in a task_requested event. Used as the
  // idempotency guard in handleTaskRequested instead of checking
  // subAgentTasksRef directly — the map is re-keyed callId → taskId by
  // handleTaskStarted, so has(callId) would return false on SSE reconnect
  // replay after the migration, causing a ghost "running" card.
  const seenTaskCallIdsRef = useRef<Set<string>>(new Set());
  const transfersRef = useRef<Map<string, IRainTransferState>>(new Map());
  const interruptOwnerSinkRef = useRef<Map<string, IMessageSink>>(new Map());

  // Keep the consumer callback in a ref so the event loop reads the latest
  // closure without the SSE effect depending on (and reconnecting for) it.
  const onVisibleToolResultRef = useRef(onVisibleToolResult);
  onVisibleToolResultRef.current = onVisibleToolResult;

  const onToolSucceededRef = useRef(onToolSucceeded);
  onToolSucceededRef.current = onToolSucceeded;

  // Guards the one-shot consume of the pending first-message stash (below).
  const pendingSeedConsumedRef = useRef(false);

  // Optimistic outgoing messages: rendered immediately on send so the user's
  // message appears without waiting for the server to echo input_text /
  // input_image / input_document back through the SSE stream. The first echo
  // of a turn drops these placeholders so the authoritative echoed message
  // takes over (see clearOptimisticMessages); a failed send removes them.
  const pendingOptimisticIdsRef = useRef<Set<string>>(new Set());

  // Ids of internal (non-UI-visible) tool calls currently in flight at root. An
  // internal tool's tool_result clears its id; `isRunningInternalTool` mirrors
  // whether the set is non-empty. Generation end (complete/failed) and stream
  // errors clear it so a missing tool_result can't wedge the indicator on.
  // toolCallId -> the event id of its `tool_call_start`, so a rollback can
  // drop calls whose result was deleted (they render no card, so the
  // message-id index can't find them).
  const activeInternalToolCallIdsRef = useRef<Map<string, number>>(new Map());

  // A single user send is echoed back as several events — input_image /
  // input_document (often first) plus input_text — sometimes interleaved with
  // hidden `reminder`/`instruction_text` events. This holds the id of the
  // outgoing bubble currently being assembled from those echoes so text and
  // attachments coalesce into one message instead of separate bubbles. Reset
  // once the assistant's response begins (see routeEventThroughSink).
  const currentInputMessageIdRef = useRef<string | null>(null);

  // Bridge the attachment-click handler through a ref so the onClick captured
  // on each echoed IChatAttachment always routes to the latest closure (the
  // attachments are built once during echo/replay and persist in `messages`).
  const onAttachmentClickRef = useRef(onAttachmentClick);
  onAttachmentClickRef.current = onAttachmentClick;

  const [isSending, setIsSending] = useState(false);

  // Backlog events + sub-agent roster come from the scope-bound adapter (the
  // adapter calls `refetchOnMount: 'always'` so each page entry forces a fresh
  // round-trip; `isFetching` — not `isLoading` — is the replay gate below).
  const backlogError = backlogErrorRaw;

  const backlogReplayedRef = useRef(false);
  const lastBackloggedEventIdRef = useRef<string | undefined>(undefined);
  // Latest event id seen across backlog + live SSE. `streamOptions.lastEventId`
  // reads this (not the frozen backlog max) so a hook-level reconnect resumes
  // from where the live stream left off instead of replaying the whole session
  // from the backlog boundary. Replaying completed turns would re-run their
  // output_text/reasoning deltas and, before the accumulator id fix, spawn
  // duplicate assistant bubbles. Updated synchronously in handleStreamEvent.
  const lastSeenEventIdRef = useRef<string | undefined>(undefined);
  const [backlogDone, setBacklogDone] = useState(false);
  /**
   * How many backlog events have already been fed through `handleStreamEvent`.
   *
   * Only used on the polling path, where the same query result is seen again and
   * again and everything before this point has already been rendered. A COUNT
   * rather than an id, because ids are not globally monotonic across sub-agent
   * events — see the replay below.
   */

  // True only while the one-shot backlog replay loop below is running. Backlog
  // events flow through the same handleStreamEvent path as live SSE, so without
  // this guard replaying a thread's historical terminal failures re-fires their
  // error toast on every mount / thread-switch / navigate-back — the "multiple
  // popups" in MAN-3766. Live-only side effects (the generation-failed toast +
  // Sentry capture) read this to stay silent during replay.
  const isReplayingBacklogRef = useRef(false);

  /**
   * Bridges so the JSX captured inside a card's `customContent` can call a
   * stable function reference and still hit the latest closure once the
   * mutation callbacks are wired up below.
   */
  const submitInterruptReplyRef = useRef<
    (interruptId: string, answers: V1QuestionAnswer[]) => Promise<void>
  >(noop as () => Promise<void>);

  const submitToolApprovalRef = useRef<
    (toolCallId: string, approved: boolean, reason?: string) => Promise<void>
  >(noop as () => Promise<void>);

  const resolveRequestConnectionRef = useRef<
    (toolCallId: string, connectionId: string) => Promise<void>
  >(noop as () => Promise<void>);

  const failRequestConnectionRef = useRef<
    (toolCallId: string) => Promise<void>
  >(noop as () => Promise<void>);

  const skipRequestConnectionRef = useRef<
    (toolCallId: string) => Promise<void>
  >(noop as () => Promise<void>);

  const resolveAttachFileRef = useRef<
    (toolCallId: string, assets: V1Asset[]) => Promise<void>
  >(noop as () => Promise<void>);

  const resolveAttachFileUploadRef = useRef<
    (toolCallId: string, files: File[]) => Promise<void>
  >(noop as () => Promise<void>);

  const failAttachFileRef = useRef<(toolCallId: string) => Promise<void>>(
    noop as () => Promise<void>,
  );

  const resolveWithValueRef = useRef<
    (toolCallId: string, content: Rainv1Value) => Promise<boolean>
  >(async () => false);

  const resolveWithDecisionRef = useRef<
    (toolCallId: string, approved: boolean) => Promise<boolean>
  >(async () => false);

  const sendFollowUpRef = useRef<(text: string) => Promise<void>>(
    noop as () => Promise<void>,
  );

  const resolveMaxIterationsRef = useRef<
    (id: string, shouldContinue: boolean) => Promise<void>
  >(noop as () => Promise<void>);

  const wakeThreadRef = useRef<() => Promise<void>>(
    noop as () => Promise<void>,
  );

  /**
   * Stable resolver handles for the tool cards. Each reads through its ref, so a
   * card built many turns ago still reaches the current closure, and the object
   * identity never changes — a renderer map memoized on it stays stable.
   */
  const resolvers = useMemo<IRainToolResolvers>(
    () => ({
      submitToolApproval: (toolCallId, approved, reason) =>
        submitToolApprovalRef.current(toolCallId, approved, reason),
      resolveRequestConnection: (toolCallId, connectionId) =>
        resolveRequestConnectionRef.current(toolCallId, connectionId),
      failRequestConnection: toolCallId =>
        failRequestConnectionRef.current(toolCallId),
      skipRequestConnection: toolCallId =>
        skipRequestConnectionRef.current(toolCallId),
      resolveAttachFile: (toolCallId, assets) =>
        resolveAttachFileRef.current(toolCallId, assets),
      resolveAttachFileUpload: (toolCallId, files) =>
        resolveAttachFileUploadRef.current(toolCallId, files),
      failAttachFile: toolCallId => failAttachFileRef.current(toolCallId),
      resolveWithValue: (toolCallId, content) =>
        resolveWithValueRef.current(toolCallId, content),
      resolveWithDecision: (toolCallId, approved) =>
        resolveWithDecisionRef.current(toolCallId, approved),
    }),
    [],
  );

  const toolRenderers = agentConfig.toolRenderers ?? EMPTY_TOOL_RENDERERS;
  const toolActivities = agentConfig.toolActivities ?? EMPTY_TOOL_ACTIVITIES;
  // Asked only when the exact-name map misses — for open-ended families like
  // `graph_*`, which cannot be enumerated. See `IRainAgentConfig`.
  const resolveToolRenderer = agentConfig.resolveToolRenderer;

  const {
    buildInterruptMessage,
    buildToolCallMessage,
    buildCompactionMessage,
    buildSleepMessage,
    buildSubAgentTaskMessage,
    buildFollowUpMessage,
    buildMaxIterationsMessage,
    buildTransferMarkerMessage,
  } = useRainMessageBuilders({
    submitInterruptReplyRef,
    submitToolApprovalRef,
    sendFollowUpRef,
    resolveMaxIterationsRef,
    wakeThreadRef,
    toolVerbosity: agentConfig.toolVerbosity ?? 'curated',
    toolRenderers,
    toolActivities,
    resolveToolRenderer,
    resolvers,
  });

  /**
   * Upserts a root message built by a lifecycle handler (task/transfer cards),
   * recording its origin for rollback. These handlers bypass the sink because
   * they build their card from a ref-held state map.
   */
  const upsertRootMessageForEvent = useCallback(
    (next: IChatMessage, eventId: string | undefined): void => {
      recordMessageOrigin(messageOriginsRef.current, next.id, eventId);
      setMessages(prev => upsertById(prev, next));
    },
    [],
  );

  // -- Root sink (writes top-level messages) --------------------------------

  const rootSink = useMemo<IMessageSink>(
    () => ({
      upsertMessage: next => {
        recordMessageOrigin(
          messageOriginsRef.current,
          next.id,
          currentEventIdRef.current,
        );
        setMessages(prev => upsertById(prev, next));
      },
      removeMessage: id => {
        messageOriginsRef.current.delete(id);
        setMessages(prev => prev.filter(m => m.id !== id));
      },
      outputAccum: rootOutputAccumRef.current,
      reasoningAccum: rootReasoningAccumRef.current,
      toolCalls: rootToolCallsRef.current,
      toolCallIndexToId: rootToolCallIndexToIdRef.current,
      interrupts: rootInterruptsRef.current,
      compactions: rootCompactionsRef.current,
      sleeps: rootSleepsRef.current,
      followUps: rootFollowUpsRef.current,
      currentFollowUpIdRef: rootCurrentFollowUpIdRef,
    }),
    [],
  );

  /**
   * Rebuilds the widgets of already-cached root tool-call cards.
   *
   * A card's appearance can depend on state the thread never streams — the run
   * view's current focus, say — and those messages were built before that state
   * changed. A surface owning such state calls this with the ids whose cards
   * need to catch up.
   */
  const rebuildToolCards = useCallback(
    (toolCallIds: readonly (string | undefined)[]): void => {
      toolCallIds.forEach(id => {
        if (!id) return;
        const state = rootToolCallsRef.current.get(id);
        if (state) rootSink.upsertMessage(buildToolCallMessage(state));
      });
    },
    [buildToolCallMessage, rootSink],
  );

  // -- Task sink factory (writes into task.nestedMessages and re-renders the
  //    parent task card via the root sink) ----------------------------------

  const buildTaskSink = useCallback(
    (taskId: string): IMessageSink => {
      const ensureTask = (): IRainSubAgentTaskState | undefined =>
        subAgentTasksRef.current.get(taskId);
      const initial = ensureTask();
      if (!initial) {
        throw new Error(
          `buildTaskSink called for unknown task ${taskId} — handleTaskStart must register the task first`,
        );
      }
      return {
        upsertMessage: next => {
          const task = ensureTask();
          if (!task) return;
          // Track both the nested message and the parent card, so a rollback
          // can drop either the one bubble or the whole task card.
          recordMessageOrigin(
            messageOriginsRef.current,
            next.id,
            currentEventIdRef.current,
          );
          task.nestedMessages = upsertById(task.nestedMessages, next);
          const card = buildSubAgentTaskMessage(task);
          recordMessageOrigin(
            messageOriginsRef.current,
            card.id,
            currentEventIdRef.current,
          );
          setMessages(prev => upsertById(prev, card));
        },
        removeMessage: id => {
          const task = ensureTask();
          if (!task) return;
          messageOriginsRef.current.delete(id);
          task.nestedMessages = task.nestedMessages.filter(m => m.id !== id);
          setMessages(prev => upsertById(prev, buildSubAgentTaskMessage(task)));
        },
        outputAccum: initial.outputAccum,
        reasoningAccum: initial.reasoningAccum,
        toolCalls: initial.toolCalls,
        toolCallIndexToId: initial.toolCallIndexToId,
        interrupts: initial.interrupts,
        compactions: initial.compactions,
        sleeps: initial.sleeps,
        followUps: initial.followUps,
        currentFollowUpIdRef: initial.currentFollowUpIdRef,
      };
    },
    [buildSubAgentTaskMessage],
  );

  // Locate the sink that owns a tool call. Bumblebee tools can fire at root or
  // inside a sub-agent task; the per-sink toolCalls map is the source of truth
  // either way. Returns null when no sink has the call.
  const findToolCallOwner = useCallback(
    (
      toolCallId: string,
    ): { sink: IMessageSink; state: IRainToolCallState } | null => {
      const rootState = rootSink.toolCalls.get(toolCallId);
      if (rootState) return { sink: rootSink, state: rootState };
      for (const task of subAgentTasksRef.current.values()) {
        const taskState = task.toolCalls.get(toolCallId);
        if (taskState) {
          return { sink: buildTaskSink(task.taskId), state: taskState };
        }
      }
      return null;
    },
    [buildTaskSink, rootSink],
  );

  // Marks every currently-open follow-up card (root + sub-agent sinks) as
  // answered so it flips to its non-interactive view-only rendering. There is
  // only ever supposed to be one active follow-up at a time; this helper is
  // the single choke point that closes the previous one whenever the user
  // starts a new turn (sending a message, replying to an interrupt, or
  // approving/denying a tool call). Once a follow-up is acted on, the picked
  // suggestion is already echoed as a user message, so the card is retired
  // (removed) rather than left behind as a disabled box.
  const closeOpenFollowUps = useCallback(() => {
    closeFollowUpForSink(rootSink);
    for (const task of subAgentTasksRef.current.values()) {
      closeFollowUpForSink(buildTaskSink(task.taskId));
    }
  }, [buildTaskSink, rootSink]);

  const collectUnansweredInterruptIds = useCallback((): Set<string> => {
    const ids = new Set<string>();
    const sinks = [
      rootSink,
      ...Array.from(subAgentTasksRef.current.values(), task =>
        buildTaskSink(task.taskId),
      ),
    ];
    for (const sink of sinks) {
      for (const [interruptId, state] of sink.interrupts) {
        if (!state.answered) ids.add(interruptId);
      }
    }
    return ids;
  }, [buildTaskSink, rootSink]);

  const closeUnansweredInterrupts = useCallback(
    (interruptIds: ReadonlySet<string>) => {
      closeUnansweredInterruptsForSink(
        rootSink,
        buildInterruptMessage,
        interruptIds,
      );
      for (const task of subAgentTasksRef.current.values()) {
        closeUnansweredInterruptsForSink(
          buildTaskSink(task.taskId),
          buildInterruptMessage,
          interruptIds,
        );
      }
    },
    [buildInterruptMessage, buildTaskSink, rootSink],
  );

  // -- Root-only handlers ---------------------------------------------------

  // Drops any optimistic outgoing placeholders. Called when the first input
  // echo of a turn arrives so the authoritative echoed message (which carries
  // the server-assigned id and is what backlog replay reproduces) replaces the
  // placeholder. No-op during backlog replay since nothing is pending then.
  const clearOptimisticMessages = useCallback(() => {
    const ids = pendingOptimisticIdsRef.current;
    if (ids.size === 0) return;
    // Snapshot the ids and reset the ref *before* setMessages. React batches
    // SSE-driven updates, so the updater below runs after this function
    // returns — if we cleared `ids` synchronously the deferred updater would
    // close over an empty set and filter nothing, leaving the optimistic
    // bubble duplicated alongside the echoed one.
    const idsToDrop = new Set(ids);
    pendingOptimisticIdsRef.current = new Set();
    setMessages(prev => prev.filter(m => !idsToDrop.has(m.id)));
  }, []);

  // Turns raw user-message content into what the chat should display: strips
  // the `<user_action>` sentinel the agent pattern-matches (e.g. the
  // "Make deterministic" trigger) and shows a friendly label instead of the
  // raw XML. Applied to both the optimistic seed and the server echo so the
  // two reconcile on identical content.
  const toUserDisplayText = useCallback(
    (raw: string) =>
      sanitizeUserActionForDisplay(raw, actionType => {
        if (actionType === 'make_deterministic')
          return t('automation.preview.makeDeterministic', {
            ns: 'automation',
          });
        if (actionType === 'review_preview')
          return t('automation.preview.reviewKickoff', { ns: 'automation' });
        if (actionType === 'upgrade_manifest')
          return t('automation.manifestSopView.upgradeBanner.action', {
            ns: 'automation',
          });
        // Step-workbench "make configurable" ask (produced by
        // `@/modules/step-workbench/hooks/use-config-promote`, whose
        // MAKE_CONFIGURABLE_ACTION_TYPE must match this literal). Reuses the
        // rail's own button copy so the chip and the button never drift. The
        // instruction is long and internal, so it must never render raw.
        if (actionType === 'make_configurable')
          return t('params.lift.action', { ns: 'step-workbench' });
        // Finance investigator kickoff (produced by
        // `@/modules/finance/investigations/open-case-message`, whose
        // OPEN_CASE_ACTION_TYPE must match this literal — same
        // literal-on-both-sides convention as the two above). The brief is long
        // and internal, so it must never render raw: without a label the
        // sanitizer falls back to showing the whole inner text.
        if (actionType === 'open_case')
          return t('finance.investigations.chat.openCaseLabel', {
            ns: 'finance',
          });
        // The bare case reference prepended to an operator's own first
        // question (CASE_CONTEXT_ACTION_TYPE in the same module). Labelled
        // for the same reason as the above: without one the sanitizer renders
        // the resource name and the tool instruction as if the operator had
        // typed them.
        if (actionType === 'case_context')
          return t('finance.investigations.chat.caseContextLabel', {
            ns: 'finance',
          });
        return undefined;
      }),
    [t],
  );

  const handleInputTextEcho = useCallback(
    (event: V1Event) => {
      const rawText = event.input_text?.text ?? '';
      if (!rawText) return;
      const outgoingContent = buildOutgoingContent(rawText, toUserDisplayText);
      // Read/write the coalescing id *synchronously*, not inside the setMessages
      // updater. Backlog replay processes events in a synchronous loop while
      // setMessages updaters are batched/deferred — reading the ref inside the
      // updater would see only the loop's final value, collapsing every turn's
      // message into one bubble. Capturing it here keeps coalescing consistent
      // with the synchronous reset in routeEventThroughSink.
      const openId = currentInputMessageIdRef.current;
      const messageId = openId ?? event.id ?? createRandomMessageId();
      currentInputMessageIdRef.current = messageId;
      recordMessageOrigin(messageOriginsRef.current, messageId, event.id);
      setMessages(prev => {
        // Merge into the bubble being assembled for this send (e.g. when an
        // attachment echo already opened it) so text and attachments share one
        // message.
        if (openId) {
          const idx = prev.findIndex(m => m.id === openId);
          if (idx !== -1) {
            const copy = prev.slice();
            copy[idx] = { ...copy[idx], ...outgoingContent };
            return copy;
          }
        }
        // Upsert (not blind append): on SSE reconnect the server re-delivers
        // events from Last-Event-ID, so the same input echo can arrive twice with
        // the same server id. Appending would add a second message under a
        // duplicate React key, corrupting reconciliation of sibling messages
        // (words from one bubble visibly displacing into another mid-stream).
        return upsertById(prev, {
          id: messageId,
          ...outgoingContent,
          direction: EMessageDirection.OUTGOING,
          type: EMessageType.DEFAULT,
        });
      });
    },
    [toUserDisplayText],
  );

  const handleInputAttachmentEcho = useCallback(
    (event: V1Event, attachment: IChatAttachment) => {
      // See handleInputTextEcho: capture the coalescing id synchronously so
      // backlog replay (synchronous loop + deferred updaters) doesn't collapse
      // separate sends into one bubble.
      const openId = currentInputMessageIdRef.current;
      const messageId = openId ?? event.id ?? createRandomMessageId();
      currentInputMessageIdRef.current = messageId;
      recordMessageOrigin(messageOriginsRef.current, messageId, event.id);
      setMessages(prev => {
        // Append to the bubble being assembled for this send so multiple
        // attachments (and the text echo) coalesce into one message.
        if (openId) {
          const idx = prev.findIndex(m => m.id === openId);
          if (idx !== -1) {
            const existing = prev[idx].attachments ?? [];
            if (existing.some(a => a.id === attachment.id)) return prev;
            const copy = prev.slice();
            copy[idx] = {
              ...copy[idx],
              attachments: [...existing, attachment],
            };
            return copy;
          }
        }
        // Upsert (not blind append): SSE reconnect re-delivers events from
        // Last-Event-ID, so the same attachment echo can arrive twice under the
        // same server id. A duplicate React key corrupts message reconciliation.
        return upsertById(prev, {
          id: messageId,
          direction: EMessageDirection.OUTGOING,
          type: EMessageType.DEFAULT,
          attachments: [attachment],
        });
      });
    },
    [],
  );

  const handleInputImageEcho = useCallback(
    (event: V1Event) => {
      const uri = event.input_image?.uri;
      if (!uri) return;
      const filename = t('rain.attachment.imageLabel');
      handleInputAttachmentEcho(event, {
        id: uri,
        name: filename,
        type: EAttachmentType.IMAGE,
        url: uri,
        onClick: () => onAttachmentClickRef.current?.({ uri, filename }),
      });
    },
    [handleInputAttachmentEcho, t],
  );

  const handleInputDocumentEcho = useCallback(
    (event: V1Event) => {
      const uri = event.input_document?.uri;
      if (!uri) return;
      const filename =
        event.input_document?.filename ?? t('rain.attachment.documentLabel');
      handleInputAttachmentEcho(event, {
        id: uri,
        name: filename,
        type: mapFileTypeToAttachmentType(detectFileType(filename)),
        url: uri,
        onClick: () => onAttachmentClickRef.current?.({ uri, filename }),
      });
    },
    [handleInputAttachmentEcho, t],
  );

  // Uploads now reach the client as a `reminder` event (the file is staged into
  // the `uploads://` store rather than echoed as input_image/input_document —
  // see attachment-reminder.ts). Parse the reminder into an attachment chip and
  // route it through the same coalescing path as the legacy echoes so it lands
  // on the send's outgoing bubble. Returns true when the reminder was an upload
  // (so the caller keeps other reminders hidden). See ENG-4666.
  const handleUploadReminderEcho = useCallback(
    (event: V1Event): boolean => {
      const upload = parseUploadReminder(event.reminder?.text);
      if (!upload) return false;
      const { uri, filename } = upload;
      handleInputAttachmentEcho(event, {
        id: uri,
        name: filename,
        type:
          upload.kind === 'image'
            ? EAttachmentType.IMAGE
            : mapFileTypeToAttachmentType(detectFileType(filename)),
        url: uri,
        onClick: () => onAttachmentClickRef.current?.({ uri, filename }),
      });
      return true;
    },
    [handleInputAttachmentEcho],
  );

  const handleGenerationFailedSideEffects = useCallback(
    (event: V1Event) => {
      const failure = event.generation_failed;
      if (failure?.kind === V1GenerationErrorKind.GENERATION_ERROR_KIND_STOPPED)
        return;
      // Historical failures replayed from the backlog were already surfaced
      // (toast + Sentry) when they happened live. Re-running these side effects
      // on every mount / thread-switch spams stale error popups (MAN-3766); the
      // failure is still rendered in the transcript either way.
      if (isReplayingBacklogRef.current) return;
      const message = failure?.message ?? '';
      toast.error(
        message
          ? t('rain.error.generationFailed', {
              reason: message,
            })
          : t('rain.error.sendMessage'),
      );
      // Retryable/non-actionable for us — log as a warning (not an error) so we
      // retain visibility into failure rates without paging on each occurrence.
      Sentry.logger.warn(
        `Rain generation failed${message ? `: ${message}` : ''}`,
        {
          module: 'rain-chat',
          action: 'generation_failed',
          kind: failure?.kind ?? 'unknown',
          attempt: failure?.attempt,
          maxAttempts: failure?.max_attempts,
          retryable: failure?.retryable,
        },
      );
    },
    [t],
  );

  // Renders (or replays) the max-iterations question card for a terminal
  // tool-loop failure. Keyed by the failing event id so SSE reconnect replay is
  // idempotent — a re-delivered failure upserts the same card rather than
  // stacking a second one, and an already-answered card keeps its frozen state.
  const handleMaxIterationsFailure = useCallback(
    (event: V1Event): void => {
      const id = event.id ?? createRandomMessageId();
      const existing = rootMaxIterationsRef.current.get(id);
      if (existing) {
        rootSink.upsertMessage(buildMaxIterationsMessage(existing));
        return;
      }
      const state: IRainMaxIterationsState = {
        id,
        turnCount: parseMaxIterationsCount(event.generation_failed?.message),
        answered: false,
        isSubmitting: false,
      };
      rootMaxIterationsRef.current.set(id, state);
      rootSink.upsertMessage(buildMaxIterationsMessage(state));
    },
    [buildMaxIterationsMessage, rootSink],
  );

  // Notifies the consumer whenever a tool's result succeeds so it can run
  // scope-specific side effects immediately (see onToolSucceeded). Takes the
  // caller's already-resolved name (event.tool_result.name may be omitted,
  // relying on the stored start — see the isVisibleTool resolution above)
  // rather than re-reading tool.name directly, so a result that omits its
  // name doesn't silently skip notifying.
  const notifyToolSucceeded = useCallback(
    (event: V1Event, resolvedName: string | undefined): void => {
      const tool = event.tool_result;
      if (!tool?.success || !resolvedName) return;
      onToolSucceededRef.current?.(resolvedName);
    },
    [],
  );

  // Stream-driven refresh for the Todo card: when the agent marks a step
  // via the plan_mark_step tool, invalidate the active-plan query so the
  // "X of Y" counter and per-step icons update on the same render tick.
  const handleActivePlanInvalidation = useCallback(
    (event: V1Event, resolvedName: string | undefined): void => {
      const tool = event.tool_result;
      if (!tool?.success) return;
      const toolName = agentConfig.planMarkStepToolName;
      if (!toolName || resolvedName !== toolName) return;
      queryClient.invalidateQueries({
        queryKey: getActivePlanQueryKey(),
      });
    },
    [agentConfig.planMarkStepToolName, getActivePlanQueryKey, queryClient],
  );

  // -- Sub-agent task lifecycle ---------------------------------------------

  const handleTaskRequested = useCallback(
    (event: V1Event) => {
      const requested = event.task_requested;
      const callId = requested?.call_id;
      if (!callId) return;
      // Guard against SSE reconnect replays using a dedicated Set. Checking
      // subAgentTasksRef.current.has(callId) is insufficient because
      // handleTaskStarted re-keys the entry from callId → taskId, so a replay
      // after that migration would pass the has() check and insert a ghost
      // "running" card that overwrites the completed/failed state.
      if (seenTaskCallIdsRef.current.has(callId)) return;
      seenTaskCallIdsRef.current.add(callId);
      const state: IRainSubAgentTaskState = {
        // taskId starts as callId (temporary) until task_started provides the
        // real sub-thread id. The stable message key always uses callId.
        taskId: callId,
        callId,
        subAgentId: requested?.sub_agent_id ?? '',
        input: requested?.input ?? '',
        status: 'running',
        startedAt: Date.now(),
        nestedMessages: [],
        outputAccum: new Map(),
        reasoningAccum: new Map(),
        toolCalls: new Map(),
        toolCallIndexToId: new Map(),
        interrupts: new Map(),
        compactions: new Map(),
        sleeps: new Map(),
        followUps: new Map(),
        currentFollowUpIdRef: { current: null },
      };
      subAgentTasksRef.current.set(callId, state);
      upsertRootMessageForEvent(buildSubAgentTaskMessage(state), event.id);
    },
    [buildSubAgentTaskMessage, upsertRootMessageForEvent],
  );

  const handleTaskStarted = useCallback((event: V1Event) => {
    const started = event.task_started;
    const taskId = started?.task_id;
    const callId = started?.call_id;
    if (!taskId || !callId) return;
    // Already migrated on a previous replay pass.
    if (subAgentTasksRef.current.has(taskId)) return;
    const state = subAgentTasksRef.current.get(callId);
    if (!state) return;
    // Re-key from call_id → task_id so scoped events routed by event.task_id
    // find the task correctly.
    subAgentTasksRef.current.delete(callId);
    state.taskId = taskId;
    subAgentTasksRef.current.set(taskId, state);
    // No card re-render needed — the message key uses callId and is unchanged.
  }, []);

  const handleTaskCompleted = useCallback(
    (event: V1Event) => {
      const completed = event.task_completed;
      const taskId = completed?.task_id;
      if (!taskId) return;
      const task = subAgentTasksRef.current.get(taskId);
      if (!task) return;
      task.status = 'success';
      task.completedAt = Date.now();
      task.result = completed?.result;
      releaseTaskStreamingState(task);
      upsertRootMessageForEvent(buildSubAgentTaskMessage(task), event.id);
    },
    [buildSubAgentTaskMessage, upsertRootMessageForEvent],
  );

  const handleTaskFailed = useCallback(
    (event: V1Event) => {
      const failed = event.task_failed;
      const taskId = failed?.task_id;
      if (!taskId) return;
      const task = subAgentTasksRef.current.get(taskId);
      if (!task) return;
      task.status = 'error';
      task.completedAt = Date.now();
      task.errorMessage = failed?.message;
      task.errorKind = failed?.kind;
      releaseTaskStreamingState(task);
      upsertRootMessageForEvent(buildSubAgentTaskMessage(task), event.id);
    },
    [buildSubAgentTaskMessage, upsertRootMessageForEvent],
  );

  // -- Transfer lifecycle ---------------------------------------------------

  const handleTransferRequested = useCallback(
    (event: V1Event) => {
      const requested = event.transfer_requested;
      const transferId = requested?.transfer_id;
      if (!transferId) return;
      // Idempotent on SSE reconnect replay.
      if (transfersRef.current.has(transferId)) return;
      const activeTransferId = activeTransferIdRef.current;
      const state: IRainTransferState = {
        transferId,
        to: requested?.to ?? '',
        from:
          (activeTransferId &&
            transfersRef.current.get(activeTransferId)?.to) ||
          rootAgentId,
        initiatedBy: requested?.initiated_by,
        message: requested?.message,
        status: 'pending',
      };
      transfersRef.current.set(transferId, state);
      upsertRootMessageForEvent(
        buildTransferMarkerMessage(state, rootAgentId),
        event.id,
      );
    },
    [buildTransferMarkerMessage, rootAgentId, upsertRootMessageForEvent],
  );

  const handleTransferCompleted = useCallback(
    (event: V1Event) => {
      const completed = event.transfer_completed;
      const transferId = completed?.transfer_id;
      if (!transferId) return;
      const state = transfersRef.current.get(transferId);
      if (!state) return;
      // Idempotent on SSE reconnect replay — if the transfer was already
      // enacted (status 'active'), re-running this handler would transiently
      // set activeAgentId to the forward agent even when a back-transfer has
      // already completed and reset it to null.
      if (state.status === 'active') return;
      const to = completed?.to ?? '';
      const isBackTransfer = !to || to === 'main' || to === rootAgentId;
      const forwardId = activeTransferIdRef.current;
      const forward =
        isBackTransfer && forwardId
          ? transfersRef.current.get(forwardId)
          : undefined;
      const updated: IRainTransferState = {
        ...state,
        status: 'active',
        // Server time only: a client-clock fallback would skew the handoff duration.
        completedAtMs: parseRecordedAtMs(event.recorded_at_ms),
        handoffStartedAtMs: forward?.completedAtMs,
      };
      transfersRef.current.set(transferId, updated);
      upsertRootMessageForEvent(
        buildTransferMarkerMessage(updated, rootAgentId),
        event.id,
      );
      if (forwardId && forward) {
        const returned: IRainTransferState = {
          ...forward,
          returnTransferId: transferId,
        };
        transfersRef.current.set(forwardId, returned);
        // Not recorded as an origin: rolling back the return must not take the forward row with it.
        setMessages(prev =>
          upsertById(prev, buildTransferMarkerMessage(returned, rootAgentId)),
        );
      }
      // Mirror synchronously: a rollback in this same event batch runs before
      // React flushes the state update below.
      activeTransferIdRef.current = isBackTransfer ? null : transferId;
      setActiveAgentId(isBackTransfer ? null : to);
    },
    [buildTransferMarkerMessage, rootAgentId, upsertRootMessageForEvent],
  );

  const handleTransferFailed = useCallback(
    (event: V1Event) => {
      const failed = event.transfer_failed;
      const transferId = failed?.transfer_id;
      if (!transferId) return;
      const state = transfersRef.current.get(transferId);
      if (!state) return;
      const updated: IRainTransferState = {
        ...state,
        status: 'failed',
        failureReason: failed?.reason,
      };
      transfersRef.current.set(transferId, updated);
      upsertRootMessageForEvent(
        buildTransferMarkerMessage(updated, rootAgentId),
        event.id,
      );
    },
    [buildTransferMarkerMessage, rootAgentId, upsertRootMessageForEvent],
  );

  // -- Internal-tool progress tracking --------------------------------------

  // Drops a finished internal tool from the in-flight set, clearing the working
  // indicator once nothing internal is left running.
  const clearInternalToolCall = useCallback(
    (toolCallId: string | undefined): void => {
      if (!toolCallId) return;
      if (
        activeInternalToolCallIdsRef.current.delete(toolCallId) &&
        activeInternalToolCallIdsRef.current.size === 0
      ) {
        setIsRunningInternalTool(false);
      }
    },
    [],
  );

  // Clears all internal-tool tracking. Called when a turn settles
  // (generation_complete/failed) or the stream errors so a tool_result that
  // never arrives can't leave the working indicator stuck on.
  const resetInternalToolTracking = useCallback((): void => {
    activeInternalToolCallIdsRef.current.clear();
    setIsRunningInternalTool(false);
  }, []);

  // -- Turn rollback (events_deleted) ---------------------------------------

  /**
   * Reconciles the transcript against a server-side turn rollback (MAN-3816).
   *
   * When an attempt fails with a retryable error, rain soft-deletes its events,
   * announces the range via `events_deleted`, and re-publishes the turn under
   * fresh ids. Unhandled, the rolled-back bubbles stay on screen and the retry
   * renders alongside them — the same message repeating once per retry.
   *
   * Drops every message built from an event in the range, then prunes the
   * derived state behind them so the retry rebuilds rather than merging into
   * stale entries.
   */
  const handleEventsDeleted = useCallback(
    (event: V1Event): void => {
      const range = parseDeletedEventRange(event.events_deleted);
      if (!range) return;

      // Before the message-index work: a plan is keyed by its creating event,
      // so a range covering only plan events removes no cards to find.
      if (rollbackPlanEvents(planStreamRef.current, range)) {
        setPlanStreamVersion(version => version + 1);
      }

      // Internal tools render no card, so they're keyed by their start event
      // rather than through the message index — and a range covering only
      // internal events produces no removed messages at all. Sweep them before
      // the early return below, or a rollback of an attempt that did nothing
      // but write artifacts leaves the working indicator pinned on.
      for (const [toolCallId, startEventId] of [
        ...activeInternalToolCallIdsRef.current,
      ]) {
        if (startEventId >= range.from && startEventId <= range.to) {
          clearInternalToolCall(toolCallId);
        }
      }

      const removedIds = collectRolledBackMessageIds(
        messageOriginsRef.current,
        range,
      );
      // Announced even with no cards removed: a hidden-only range still has
      // tool calls the reducer must forget.
      if (removedIds.size === 0) {
        dispatchGeneration({
          type: 'rollback',
          interruptIds: [],
          approvalIds: [],
          sleepIds: [],
          compactionIds: [],
          deletedEventRange: range,
        });
        return;
      }

      // Pause ids whose card just went away. Their resolving events were
      // deleted too, so the generation reducer has to forget them or the
      // pause never clears (see the 'rollback' action).
      const removedInterruptIds: string[] = [];
      const removedApprovalIds: string[] = [];
      const removedSleepIds: string[] = [];
      const removedCompactionIds: string[] = [];

      // Sink maps are keyed by domain id; each card's message id is the
      // builder's prefix + key, so that derivation is what matches them up.
      const pruneSinkState = (sink: IMessageSink): void => {
        // Every removed tool card, not just the ones flagged
        // awaitingApproval: the flag is cleared by a grant/deny that the
        // rollback may itself have deleted, so filtering on it would miss
        // those. Deleting an absent id from the pending set is a no-op.
        removedApprovalIds.push(
          ...pruneStateByMessageId(
            sink.toolCalls,
            removedIds,
            key => `${TOOL_CALL_ID_PREFIX}${key}`,
          ),
        );
        removedInterruptIds.push(
          ...pruneStateByMessageId(
            sink.interrupts,
            removedIds,
            key => `${INTERRUPT_ID_PREFIX}${key}`,
          ),
        );
        removedCompactionIds.push(
          ...pruneStateByMessageId(
            sink.compactions,
            removedIds,
            key => `${COMPACTION_ID_PREFIX}${key}`,
          ),
        );
        removedSleepIds.push(
          ...pruneStateByMessageId(
            sink.sleeps,
            removedIds,
            key => `${SLEEP_ID_PREFIX}${key}`,
          ),
        );
        pruneStateByMessageId(
          sink.followUps,
          removedIds,
          key => `${FOLLOW_UP_ID_PREFIX}${key}`,
        );
        // Accumulators are keyed by task scope, not message id. Any open
        // bubble belongs to the rolled-back turn, so clear all of them —
        // otherwise the retry's first delta appends to a removed bubble.
        sink.outputAccum.clear();
        sink.reasoningAccum.clear();
        sink.toolCallIndexToId.clear();
        if (sink.currentFollowUpIdRef.current) {
          const openFollowUpMessageId = `${FOLLOW_UP_ID_PREFIX}${sink.currentFollowUpIdRef.current}`;
          if (removedIds.has(openFollowUpMessageId)) {
            sink.currentFollowUpIdRef.current = null;
          }
        }
      };

      pruneSinkState(rootSink);
      for (const task of subAgentTasksRef.current.values()) {
        task.nestedMessages = dropMessagesById(task.nestedMessages, removedIds);
        pruneSinkState(buildTaskSink(task.taskId));
      }

      // Retire rolled-back tasks so a retried task_requested builds a fresh
      // card instead of being swallowed by the seen-call-ids guard.
      for (const [key, task] of [...subAgentTasksRef.current]) {
        if (removedIds.has(`${SUB_AGENT_TASK_ID_PREFIX}${task.callId}`)) {
          subAgentTasksRef.current.delete(key);
          seenTaskCallIdsRef.current.delete(task.callId);
        }
      }

      pruneStateByMessageId(
        rootMaxIterationsRef.current,
        removedIds,
        key => `${MAX_ITERATIONS_ID_PREFIX}${key}`,
      );

      pruneStateByMessageId(
        transfersRef.current,
        removedIds,
        key => `${TRANSFER_MARKER_ID_PREFIX}${key}`,
      );
      // Only when the rolled-back transfer is the very one that put us on the
      // current agent — matched by id, since a later transfer to the same
      // agent is a different transfer. The back-transfer that would have reset
      // this was deleted with it, so nothing else ever would.
      if (
        activeTransferIdRef.current &&
        removedIds.has(
          `${TRANSFER_MARKER_ID_PREFIX}${activeTransferIdRef.current}`,
        )
      ) {
        activeTransferIdRef.current = null;
        setActiveAgentId(null);
      }
      // A rolled-back return hands control back to the agent it ended.
      for (const [key, transfer] of transfersRef.current) {
        if (
          !transfer.returnTransferId ||
          transfersRef.current.has(transfer.returnTransferId)
        )
          continue;
        const reopened: IRainTransferState = {
          ...transfer,
          returnTransferId: undefined,
        };
        transfersRef.current.set(key, reopened);
        activeTransferIdRef.current = key;
        setActiveAgentId(reopened.to);
        setMessages(prev =>
          upsertById(prev, buildTransferMarkerMessage(reopened, rootAgentId)),
        );
      }

      // Interrupt owners for retired interrupts (their sink may also be a task
      // sink that was just deleted, which would make a reply silently no-op).
      for (const interruptId of removedInterruptIds) {
        interruptOwnerSinkRef.current.delete(interruptId);
      }

      // Otherwise the retry's input echo coalesces into a removed bubble.
      if (
        currentInputMessageIdRef.current &&
        removedIds.has(currentInputMessageIdRef.current)
      ) {
        currentInputMessageIdRef.current = null;
      }

      dispatchGeneration({
        type: 'rollback',
        interruptIds: removedInterruptIds,
        approvalIds: removedApprovalIds,
        sleepIds: removedSleepIds,
        compactionIds: removedCompactionIds,
        deletedEventRange: range,
      });

      setMessages(prev => dropMessagesById(prev, removedIds));

      Sentry.logger.info('Rain turn rolled back', {
        module: 'rain-chat',
        action: 'events_deleted',
        agentId: rootAgentId,
        threadId: rainThreadId,
        fromEventId: range.from,
        toEventId: range.to,
        removedMessageCount: removedIds.size,
      });
    },
    [
      buildTaskSink,
      buildTransferMarkerMessage,
      clearInternalToolCall,
      rainThreadId,
      rootAgentId,
      rootSink,
    ],
  );

  // -- Client-side remote-tool fulfillment ----------------------------------

  // Auto-executes a remote tool call whose result the client must compute (the
  // Builder's `set_*` / `search_integrations`), then posts the result back so
  // the paused agent loop resumes. Inert unless the agent config wires
  // `onRemoteToolCall` + `resolvesToolCall`. Fires exactly once per call:
  // `isSubmitting` guards the window before Rain echoes the result event (which
  // flips the card to success/error via applyToolResultToSink).
  //
  // Called after each tool_call_start / tool_call_argument for a visible tool,
  // so it fires as soon as the required args have finished streaming — mirroring
  // how the `request_connection` card becomes actionable once `appName` +
  // `appVersion` are present, but without waiting on a user click.
  const maybeFulfillRemoteToolCall = useCallback(
    (sink: IMessageSink, toolCallId: string): void => {
      const handler = onRemoteToolCallRef.current;
      const resolves = resolvesToolCallRef.current;
      if (!handler || !resolves) return;
      // Never re-execute while replaying history: a previously-fulfilled call's
      // start/argument events flow through here before its (server-echoed)
      // tool_result, so firing during replay would re-run the side effect
      // (re-write the memory block, re-post the result). The live stream picks
      // up any genuinely-unresolved call after replay completes.
      if (isReplayingBacklogRef.current) return;

      const state = sink.toolCalls.get(toolCallId);
      if (!state || state.status !== 'running' || state.isSubmitting) return;
      if (!resolves(state.name, state.args)) return;

      // Mark submitting so re-entry (subsequent arg events, re-render) can't
      // double-fire before Rain's echo lands.
      const pending: IRainToolCallState = { ...state, isSubmitting: true };
      sink.toolCalls.set(toolCallId, pending);
      sink.upsertMessage(buildToolCallMessage(pending));

      void (async () => {
        try {
          // Rain emits a call's arguments together, but they reach the sink one
          // event at a time: once the required ones are in, optional ones may
          // still be queued behind them. Yield a tick and read the latest args
          // so an optional argument (e.g. traversal seeds) is not dropped.
          await new Promise(resolve => setTimeout(resolve, 100));
          const latest = sink.toolCalls.get(toolCallId) ?? pending;
          const result = await handler({
            id: state.id,
            name: state.name,
            args: latest.args,
          });
          if (cancelledRef.current) return;
          // A handler that declines (returns void) leaves the call untouched —
          // revert the optimistic isSubmitting so a card-based resolver (or a
          // later retry) can still act on it.
          if (!result) {
            const reverted: IRainToolCallState = {
              ...pending,
              isSubmitting: false,
            };
            sink.toolCalls.set(toolCallId, reverted);
            sink.upsertMessage(buildToolCallMessage(reverted));
            return;
          }
          await sendToolResultToAdapter({
            tool_call_id: toolCallId,
            success: result.success,
            ...(result.content !== undefined
              ? { content: { string_value: result.content } }
              : {}),
            ...(result.error !== undefined ? { error: result.error } : {}),
          });
          // Leave isSubmitting:true until Rain echoes a tool_result event back —
          // applyToolResultToSink flips the card to success/error.
        } catch (error) {
          // Leaving the chat (e.g. for Activity) unmounts it mid-call, and the
          // remount re-fulfils calls whose first result Rain has not echoed
          // yet; Rain then answers 409 (or 404, once the call is no longer
          // waiting) because the call is already resolved. Neither is a
          // failure the user can act on.
          const status = (
            error as { response?: { status?: number } } | undefined
          )?.response?.status;
          const alreadyResolved =
            status === 409 || status === 404 || /\b409\b/.test(String(error));
          if (cancelledRef.current || alreadyResolved) return;
          const reverted: IRainToolCallState = {
            ...pending,
            isSubmitting: false,
          };
          sink.toolCalls.set(toolCallId, reverted);
          sink.upsertMessage(buildToolCallMessage(reverted));
          toast.error(t('rain.error.sendMessage'));
          captureException(error as Error, {
            tags: { module: 'rain-chat', action: 'fulfill_remote_tool_call' },
          });
        }
      })();
    },
    [buildToolCallMessage, sendToolResultToAdapter, t],
  );

  // -- Dispatch (root or task sink) -----------------------------------------

  const routeEventThroughSink = useCallback(
    (sink: IMessageSink, event: V1Event, isRoot: boolean): void => {
      const taskScope = getEventTaskScope(event);
      // Stamps an opening assistant/reasoning stretch. Absent on synthetic
      // events (tests) and ephemeral notifications — see `parseRecordedAtMs`.
      const recordedAtMs = parseRecordedAtMs(event.recorded_at_ms);
      const registerInterruptOwner = (interruptId: string) => {
        interruptOwnerSinkRef.current.set(interruptId, sink);
      };

      // Close the assistant text bubble being streamed once a turn-boundary
      // event arrives, so text before and after an interrupt / tool call / etc.
      // renders as separate messages instead of gluing into one stranded
      // paragraph.
      sealAssistantTextOnBoundary(sink, taskScope, event);

      if (
        isRoot &&
        (event.input_text || event.input_image || event.input_document)
      ) {
        closeOpenFollowUps();
      }

      // Close a stale follow-up card the instant new activity proves a fresh
      // round has started. closeOpenFollowUps' explicit call sites (send a
      // message, reply to an interrupt, approve/deny a tool call) only cover
      // USER-initiated turns; the agent can just as easily keep going on its
      // own after a completed turn (the generation reducer's own
      // "reactivate" case — see generation-state.ts) with no user action to
      // hook into at all, which otherwise left a stale card's pills on screen
      // through however many further rounds the agent ran. `follow_up`
      // itself must not close its own card (successive follow_up events
      // coalesce into one); `reminder`/`instruction_text` are hidden system
      // events with no visible activity of their own; and
      // `generation_complete`/`generation_failed` are the END of the very
      // turn that produced this card, not a new one — the pills are supposed
      // to persist right after the agent finishes talking, so they must not
      // close on the event that settles their own turn.
      if (
        sink.currentFollowUpIdRef.current &&
        !event.follow_up &&
        !event.reminder &&
        !event.instruction_text &&
        !event.generation_complete &&
        !event.generation_failed
      ) {
        closeFollowUpForSink(sink);
      }

      // Close the outgoing bubble being assembled from input echoes once the
      // assistant's response begins. `reminder`/`instruction_text` are hidden
      // system events that can interleave with input echoes for a single send,
      // so they must not close the bubble.
      if (
        isRoot &&
        !event.input_text &&
        !event.input_image &&
        !event.input_document &&
        !event.reminder &&
        !event.instruction_text
      ) {
        currentInputMessageIdRef.current = null;
      }

      if (event.output_text) {
        appendDeltaIntoSink(
          sink.outputAccum,
          taskScope,
          event.output_text.text ?? '',
          EMessageType.DEFAULT,
          sink,
          event.id,
          recordedAtMs,
        );
        return;
      }
      if (event.reasoning_text) {
        appendDeltaIntoSink(
          sink.reasoningAccum,
          taskScope,
          event.reasoning_text.text ?? '',
          EMessageType.THINKING,
          sink,
          event.id,
          recordedAtMs,
        );
        return;
      }
      if (event.reasoning_summary) {
        appendDeltaIntoSink(
          sink.reasoningAccum,
          taskScope,
          event.reasoning_summary.text ?? '',
          EMessageType.THINKING,
          sink,
          event.id,
          recordedAtMs,
        );
        return;
      }
      if (event.input_text) {
        if (isRoot) {
          clearOptimisticMessages();
          handleInputTextEcho(event);
        }
        return;
      }
      if (event.input_image) {
        if (isRoot) {
          clearOptimisticMessages();
          handleInputImageEcho(event);
        }
        return;
      }
      if (event.input_document) {
        if (isRoot) {
          clearOptimisticMessages();
          handleInputDocumentEcho(event);
        }
        return;
      }
      if (event.tool_call_start) {
        // Only externally-visible tools get a card. Hidden tools are never
        // registered in the sink, so their follow-up tool_call_argument events
        // (resolved by index against toolCallIndexToId) self-discard as well.
        if (isVisibleTool(event.tool_call_start.name)) {
          applyToolCallStart(
            sink,
            event,
            buildToolCallMessage,
            agentConfig.clientResolvedTools,
          );
          // A remote tool with no arguments (or whose sole args rode in on the
          // start) may already be ready to fulfill.
          if (event.tool_call_start.id) {
            maybeFulfillRemoteToolCall(sink, event.tool_call_start.id);
          }
        } else if (isRoot && event.tool_call_start.id) {
          reportDroppedToolCall(event.tool_call_start.name);
          // Internal tools render no card; track them at root so RainChat can
          // show a generic working indicator while they run (ENG-4067). A
          // sub-agent's own task card already conveys its running state, so
          // task-scoped internal tools are left untracked.
          const startEventId = Number(event.id);
          activeInternalToolCallIdsRef.current.set(
            event.tool_call_start.id,
            Number.isFinite(startEventId) ? startEventId : -1,
          );
          setIsRunningInternalTool(true);
        }
        return;
      }
      if (event.tool_call_argument) {
        applyToolCallArgument(sink, event, buildToolCallMessage);
        // Resolve the call the argument targets (by index within this scope,
        // the same mapping applyToolCallArgument uses) and try to fulfill now
        // that another arg has landed.
        const argIndex = event.tool_call_argument.index;
        if (argIndex !== undefined) {
          const argToolCallId = sink.toolCallIndexToId.get(
            `${taskScope}:${argIndex}`,
          );
          if (argToolCallId) {
            maybeFulfillRemoteToolCall(sink, argToolCallId);
          }
        }
        return;
      }
      if (event.tool_result) {
        // Resolve the tool name the same way applyToolResultToSink does — the
        // result may omit it and rely on the stored start. A hidden tool has no
        // stored start and a hidden name, so it's skipped.
        const toolName =
          event.tool_result.name ??
          sink.toolCalls.get(event.tool_result.tool_call_id ?? '')?.name;
        if (isVisibleTool(toolName)) {
          applyToolResultToSink(sink, event, buildToolCallMessage);
          if (isRoot) {
            const toolCallId = event.tool_result.tool_call_id;
            const state = sink.toolCalls.get(toolCallId ?? '');
            if (toolName && state) onVisibleToolResultRef.current?.(state);
          }
        } else if (isRoot) {
          // An internal tool finished — stop tracking it (clears the working
          // indicator once nothing internal is left in flight).
          clearInternalToolCall(event.tool_result.tool_call_id);
        }
        // Notify thread-wide, regardless of root/sub-agent or card visibility
        // (artifact_write/artifact_edit are hidden tools).
        notifyToolSucceeded(event, toolName);
        // A plan_mark_step result must refresh the live Todo card now.
        handleActivePlanInvalidation(event, toolName);
        return;
      }
      if (event.interrupt) {
        applyInterrupt(
          sink,
          event,
          buildInterruptMessage,
          registerInterruptOwner,
        );
        return;
      }
      if (event.interrupt_answer) {
        applyInterruptAnswer(sink, event, buildInterruptMessage);
        return;
      }
      if (event.compact_start) {
        applyCompactStart(sink, event, buildCompactionMessage);
        return;
      }
      if (event.compact_complete) {
        applyCompactComplete(sink, event, buildCompactionMessage);
        return;
      }
      if (event.compact_failed) {
        applyCompactFailed(sink, event, buildCompactionMessage);
        return;
      }
      if (event.sleep_requested) {
        applySleepRequested(sink, event, buildSleepMessage);
        return;
      }
      if (event.sleep_completed) {
        applySleepCompleted(sink, event, buildSleepMessage);
        return;
      }
      if (event.generation_complete) {
        sink.outputAccum.delete(taskScope);
        sink.reasoningAccum.delete(taskScope);
        if (isRoot) {
          resetInternalToolTracking();
          sink.upsertMessage({
            id: `rain-complete-${event.id ?? createRandomMessageId()}`,
            direction: EMessageDirection.INCOMING,
            type: EMessageType.COMPLETE,
          });
        }
        return;
      }
      if (event.generation_failed) {
        sink.outputAccum.delete(taskScope);
        sink.reasoningAccum.delete(taskScope);
        if (isRoot) {
          // Retryable failures are the agent loop parking between auto-retry
          // attempts — not terminal. Suppress the toast / internal-tool reset;
          // the loop will reissue and the next event will land naturally.
          // (The generation-state reducer already gates retryable failures.)
          if (event.generation_failed.retryable !== true) {
            resetInternalToolTracking();
            // The tool-loop iteration cap (MAN-3769) is a soft limit the backend
            // already re-arms on request — surface it as a question card the user
            // can act on, not the generic failure toast.
            if (isMaxIterationsFailure(event.generation_failed)) {
              handleMaxIterationsFailure(event);
              return;
            }
            // A user-initiated stop surfaces a muted "You stopped this response"
            if (
              event.generation_failed.kind ===
              V1GenerationErrorKind.GENERATION_ERROR_KIND_STOPPED
            ) {
              sink.upsertMessage(
                buildStoppedMessage(
                  `rain-stopped-${event.id ?? createRandomMessageId()}`,
                ),
              );
            }
            handleGenerationFailedSideEffects(event);
          }
        }
        return;
      }
      if (event.tool_approval_requested) {
        applyToolApprovalRequested(sink, event, buildToolCallMessage);
        return;
      }
      if (event.tool_approval_granted) {
        applyToolApprovalGranted(sink, event, buildToolCallMessage);
        return;
      }
      if (event.tool_approval_denied) {
        applyToolApprovalDenied(sink, event, buildToolCallMessage);
        return;
      }
      if (event.follow_up) {
        applyFollowUp(sink, event, buildFollowUpMessage);
        return;
      }
      if (event.reminder) {
        // An upload reminder is the only user-visible reminder: it carries the
        // metadata for a file the user attached to this send, which we render
        // as a chip on the outgoing bubble. Every other reminder (plan nudges,
        // completion summaries) is system-only LLM context and stays hidden.
        if (isRoot) handleUploadReminderEcho(event);
        return;
      }
      // `instruction_text` is system-only LLM context that the backend injects
      // into the prompt; it is intentionally hidden from the UI.
      if (event.instruction_text) {
        return;
      }
    },
    [
      buildCompactionMessage,
      buildFollowUpMessage,
      buildInterruptMessage,
      buildSleepMessage,
      buildToolCallMessage,
      clearInternalToolCall,
      clearOptimisticMessages,
      closeOpenFollowUps,
      notifyToolSucceeded,
      handleActivePlanInvalidation,
      handleGenerationFailedSideEffects,
      handleMaxIterationsFailure,
      handleInputDocumentEcho,
      handleInputImageEcho,
      handleInputTextEcho,
      handleUploadReminderEcho,
      isVisibleTool,
      maybeFulfillRemoteToolCall,
      resetInternalToolTracking,
    ],
  );

  /**
   * Emits an `info` log for every event received on the thread SSE stream
   * (`.../agents/{agentId}/threads/{threadId}/stream`), one line per event.
   *
   * Envelope only — no payload field is read, so nothing the event carries
   * (prompts, model output, reasoning, tool arguments) can reach Sentry.
   * Backlog replay is skipped: those events were already logged when they
   * arrived live, and re-logging them on every mount / thread-switch would
   * multiply log volume by navigation count.
   *
   * Never throws into the event path — a telemetry failure must not break
   * message rendering.
   */
  const logStreamEvent = useCallback(
    (event: V1Event, frameEventId?: string): void => {
      if (isReplayingBacklogRef.current) return;
      try {
        const meta = buildRainEventLogMeta(event, frameEventId);
        Sentry.logger.info(`Rain stream event: ${meta.eventType}`, {
          module: 'rain-chat',
          action: 'stream_thread_events',
          agentId: rootAgentId,
          threadId: rainThreadId,
          ...meta,
        });
      } catch {
        // Ignore — logging must not affect stream handling.
      }
    },
    [rainThreadId, rootAgentId],
  );

  const handleStreamEvent = useCallback(
    // `meta` is supplied by useStream's onMessage (SSE frame id / type); backlog
    // replay calls this with the event alone.
    (event: V1Event, meta?: IMessageMeta) => {
      if (cancelledRef.current) return;

      // Track the newest event id so a reconnect resumes from here (see
      // lastSeenEventIdRef / streamOptions.lastEventId). Backlog replay also
      // flows through here, so this is >= the backlog max before live events.
      if (event.id !== undefined) {
        lastSeenEventIdRef.current = event.id;
      }

      // Id-less broadcast-only notification: reconcile and stop, so it never
      // reaches the generation reducer or the sinks as thread content.
      if (event.events_deleted) {
        logStreamEvent(event, meta?.eventId);
        handleEventsDeleted(event);
        return;
      }

      if (applyPlanEvent(planStreamRef.current, event)) {
        setPlanStreamVersion(version => version + 1);
      }

      // Every upsert triggered while routing this event attributes to it.
      currentEventIdRef.current = event.id;
      try {
        // Resolve scope before dispatching so the reducer ignores non-root
        // turn-settling events. Mirrors the routing decision below.
        const scopedTaskId = event.task_id;
        const isSubAgentEvent = Boolean(
          scopedTaskId && subAgentTasksRef.current.has(scopedTaskId),
        );
        dispatchGeneration({ type: 'event', event, isRoot: !isSubAgentEvent });

        logStreamEvent(event, meta?.eventId);

        // Sub-agent lifecycle events are root-level (task_requested spawns the
        // task; task_started migrates the key; subsequent events with matching
        // task_id stream into the task).
        if (event.task_requested) {
          handleTaskRequested(event);
          return;
        }
        if (event.task_started) {
          handleTaskStarted(event);
          return;
        }
        if (event.task_completed) {
          handleTaskCompleted(event);
          return;
        }
        if (event.task_failed) {
          handleTaskFailed(event);
          return;
        }

        // Transfer lifecycle events are root-level.
        if (event.transfer_requested) {
          handleTransferRequested(event);
          return;
        }
        if (event.transfer_completed) {
          handleTransferCompleted(event);
          return;
        }
        if (event.transfer_failed) {
          handleTransferFailed(event);
          return;
        }

        if (isSubAgentEvent && scopedTaskId) {
          const sink = buildTaskSink(scopedTaskId);
          routeEventThroughSink(sink, event, false);
          return;
        }

        routeEventThroughSink(rootSink, event, true);
      } finally {
        currentEventIdRef.current = undefined;
      }
    },
    [
      buildTaskSink,
      handleEventsDeleted,
      handleTaskCompleted,
      handleTaskFailed,
      handleTaskRequested,
      handleTaskStarted,
      handleTransferCompleted,
      handleTransferFailed,
      handleTransferRequested,
      logStreamEvent,
      rootSink,
      routeEventThroughSink,
    ],
  );

  const handleStreamError = useCallback(
    (streamError: IStreamError) => {
      // Reset generation state — any in-flight turn, pending compactions,
      // unresolved interrupts/approvals/sleeps that were tracked locally
      // are wiped. The next reconnect's backlog (or fresh SSE events) will
      // re-derive correct state.
      dispatchGeneration({ type: 'reset' });
      resetInternalToolTracking();

      setMessages(prev => [
        ...prev,
        {
          id: `rain-stream-error-${createRandomMessageId()}`,
          content: t('rain.error.threadConnectionLost'),
          direction: EMessageDirection.INCOMING,
          type: EMessageType.DEFAULT,
          status: EMessageStatus.ERROR,
        },
      ]);

      captureException(streamError.error, {
        tags: { module: 'rain-chat', action: 'stream_thread_events' },
        extra: {
          errorType: streamError.type,
          reason: streamError.reason,
          httpStatus: streamError.httpStatus,
        },
      });
    },
    [resetInternalToolTracking, t],
  );

  // Replay the REST-fetched backlog through the same event handler the SSE
  // stream uses, so both transports share one code path. The ref guard makes
  // this idempotent across re-renders (and StrictMode's double-invoke). The
  // captured max id is what `Last-Event-ID` resumes from when SSE opens.
  // eslint-disable-next-line no-restricted-syntax -- reason: One-shot replay; guarded by backlogReplayedRef and cancelledRef
  useEffect(() => {
    /*
     * Replayed ONCE; the SSE stream carries everything after it. Idempotent
     * regardless: every sink upserts by event id, which is what makes a reload
     * render the same conversation rather than a doubled one.
     */
    if (backlogReplayedRef.current) return;
    if (isFetchingBacklog) return;
    if (cancelledRef.current) return;
    // On error, mark processed so we don't loop, but leave backlogDone false
    // so the SSE stream stays gated and the body renders the error UI.
    if (backlogError) {
      backlogReplayedRef.current = true;
      return;
    }
    backlogReplayedRef.current = true;

    const allEvents = backlogData?.data.events ?? [];
    // Mark the whole loop as replay so live-only side effects (error toast +
    // Sentry capture) stay silent for historical events. The loop is
    // synchronous, so no live SSE event can interleave; finally guarantees the
    // flag is cleared even on the mid-loop cancellation return below.
    isReplayingBacklogRef.current = true;
    try {
      for (const ev of allEvents) {
        if (cancelledRef.current) return;
        handleStreamEvent(ev);
      }
    } finally {
      isReplayingBacklogRef.current = false;
    }
    // GetEvents returns chronologically ordered events and the server assigns
    // monotonically increasing ids, so the last event's id is the max.
    lastBackloggedEventIdRef.current = allEvents.at(-1)?.id;

    setBacklogDone(true);

    // Post-replay sweep for client-fulfilled remote tools: a call left pending
    // when the room was last closed (its start/args in the backlog, but no
    // tool_result yet) won't re-emit those events on the live stream, so the
    // per-event fulfillment above can't re-trigger it. Fulfillment was
    // suppressed during replay; now that the flag is cleared, resolve any such
    // still-running call so the paused agent loop can resume. Inert unless the
    // agent config wires the fulfillment seam.
    if (onRemoteToolCallRef.current) {
      for (const [id, call] of rootSink.toolCalls) {
        if (call.status === 'running' && !call.isSubmitting) {
          maybeFulfillRemoteToolCall(rootSink, id);
        }
      }
      for (const task of subAgentTasksRef.current.values()) {
        const taskSink = buildTaskSink(task.taskId);
        for (const [id, call] of task.toolCalls) {
          if (call.status === 'running' && !call.isSubmitting) {
            maybeFulfillRemoteToolCall(taskSink, id);
          }
        }
      }
    }
  }, [
    buildTaskSink,
    isFetchingBacklog,
    backlogData,
    backlogError,
    handleStreamEvent,
    maybeFulfillRemoteToolCall,
    rootSink,
  ]);

  const streamOptions = useMemo(
    () =>
      rainThreadId && backlogDone && streamUrl
        ? {
            url: streamUrl,
            protocol: EStreamProtocol.SSE,
            // Resume from the newest event actually seen (live SSE advances this
            // past the backlog boundary), not the frozen backlog max. `useStream`
            // only reads this at (re)connect time and only reconnects on
            // url/protocol change, so this memo is recomputed with a fresh value
            // exactly when a reconnect can occur.
            lastEventId:
              lastSeenEventIdRef.current ?? lastBackloggedEventIdRef.current,
            auth: {
              getToken: () => getAccessTokenSilently(),
              e2eTestMode: isE2ETestMode(),
            },
            retry: {
              strategy: ERetryStrategy.EXPONENTIAL,
              maxAttempts: STREAM_RETRY_MAX_ATTEMPTS,
              baseDelayMs: STREAM_RETRY_BASE_DELAY_MS,
              maxDelayMs: STREAM_RETRY_MAX_DELAY_MS,
            },
            observability: {
              module: 'rain-chat',
              operation: 'stream_thread_events',
            },
            onMessage: handleStreamEvent,
            onError: handleStreamError,
          }
        : null,
    [
      rainThreadId,
      backlogDone,
      streamUrl,
      getAccessTokenSilently,
      handleStreamEvent,
      handleStreamError,
    ],
  );

  useStream<V1Event>(streamOptions);

  // eslint-disable-next-line no-restricted-syntax -- reason: Cancellation guard lifecycle (set on mount, flip on unmount) for async stream callbacks
  useEffect(() => {
    cancelledRef.current = false;
    return () => {
      cancelledRef.current = true;
    };
  }, []);

  // Renders the user's outgoing message immediately and registers it as a
  // pending optimistic id, so the server's input echo (clearOptimisticMessages)
  // reconciles it. Returns the optimistic id so the caller can flip it to SENT
  // or drop it. Shared by `sendMessageToThread` and the first-message seed.
  const seedOptimisticOutgoing = useCallback(
    (content: string, attachments: IRainSendAttachment[]): string => {
      const optimisticAttachments: IChatAttachment[] = attachments.map(att => {
        const filename =
          att.kind === 'image'
            ? t('rain.attachment.imageLabel')
            : (att.filename ?? t('rain.attachment.documentLabel'));
        return {
          id: att.uri,
          name: filename,
          type:
            att.kind === 'image'
              ? EAttachmentType.IMAGE
              : mapFileTypeToAttachmentType(detectFileType(att.filename ?? '')),
          url: att.uri,
          onClick: () =>
            onAttachmentClickRef.current?.({ uri: att.uri, filename }),
        };
      });
      const optimisticId = createRandomMessageId();
      pendingOptimisticIdsRef.current.add(optimisticId);
      setMessages(prev => [
        ...prev,
        {
          id: optimisticId,
          ...buildOutgoingContent(content, toUserDisplayText),
          direction: EMessageDirection.OUTGOING,
          type: EMessageType.DEFAULT,
          status: EMessageStatus.SENDING,
          ...(optimisticAttachments.length > 0
            ? { attachments: optimisticAttachments }
            : {}),
        },
      ]);
      return optimisticId;
    },
    [t, toUserDisplayText],
  );

  const sendMessageToThread = useCallback(
    async (draft: IRainMessageDraft): Promise<boolean> => {
      const content = (draft.content ?? '').trim();
      const attachments = draft.attachments ?? [];
      if (!content && attachments.length === 0) return false;

      setIsSending(true);

      const message = buildRainUserMessage(content, attachments);

      // Optimistically render the user's message right away. It's reconciled
      // against the server's input echo (clearOptimisticMessages) on success
      // and removed on failure.
      const optimisticId = seedOptimisticOutgoing(content, attachments);
      // Snapshot before the await so an interrupt the new turn streams in early isn't skipped.
      const redirectedInterruptIds = collectUnansweredInterruptIds();

      try {
        // HTTP acceptance remains true even if the user switches threads while sending.
        await sendMessageToAdapter({ message });
        if (cancelledRef.current) return true;
        // Send accepted — start a new turn. Flip the optimistic placeholder to
        // SENT; the server echo (input_text/input_image/input_document) will
        // shortly clear it and render the authoritative message.
        setMessages(prev =>
          prev.map(m =>
            m.id === optimisticId ? { ...m, status: EMessageStatus.SENT } : m,
          ),
        );
        closeUnansweredInterrupts(redirectedInterruptIds);
        // Echoes start the turn and seal old text; late acceptance must preserve their reply and completion.
        if (pendingOptimisticIdsRef.current.has(optimisticId)) {
          closeOpenFollowUps();
          rootOutputAccumRef.current.delete(ROOT_TASK_SCOPE);
          dispatchGeneration({ type: 'start-turn' });
        }
        return true;
      } catch (error) {
        // Send rejected — no echo will arrive, so drop the placeholder.
        pendingOptimisticIdsRef.current.delete(optimisticId);
        if (cancelledRef.current) return false;
        setMessages(prev => prev.filter(m => m.id !== optimisticId));
        errorHandler(error, extracted => {
          toast.error(t('rain.error.sendMessage'), {
            description: extracted?.message,
          });
        });
        captureException(error as Error, {
          tags: { module: 'rain-chat', action: 'send_message' },
        });
        return false;
      } finally {
        if (!cancelledRef.current) setIsSending(false);
      }
    },
    [
      closeOpenFollowUps,
      closeUnansweredInterrupts,
      collectUnansweredInterruptIds,
      seedOptimisticOutgoing,
      sendMessageToAdapter,
      t,
    ],
  );

  const submitInterruptReply = useCallback(
    async (interruptId: string, answers: V1QuestionAnswer[]) => {
      const registeredOwner = interruptOwnerSinkRef.current.get(interruptId);
      const ownerSink = registeredOwner ?? rootSink;
      const existing = ownerSink.interrupts.get(interruptId);
      if (!existing) {
        // Either applyInterrupt never registered an owner (event arrived before
        // the task was set up, or the map was cleared mid-flight), or the owner
        // was registered but the interrupt has since been evicted. Either case
        // is a contract drift worth a Sentry breadcrumb — we silently no-op in
        // production but want visibility when it happens.
        captureMessage(
          'Rain interrupt reply targeted a sink without the matching interrupt',
          {
            level: 'warning',
            tags: { module: 'rain-chat', action: 'submit_interrupt_reply' },
            extra: { interruptId, ownerWasRegistered: !!registeredOwner },
          },
        );
        return;
      }

      const pending: IRainInterruptState = { ...existing, isSubmitting: true };
      ownerSink.interrupts.set(interruptId, pending);
      ownerSink.upsertMessage(buildInterruptMessage(pending));
      // Interrupt reply starts a new generation turn — close any open
      // follow-up card so the next batch opens a fresh one.
      closeOpenFollowUps();
      setIsSending(true);
      // Optimistically resume the turn — the server's interrupt_answer echo
      // would also drive this, but it can arrive after the API resolves so
      // we'd otherwise drop the Stop button between.
      dispatchGeneration({ type: 'start-turn' });

      try {
        await sendMessageToAdapter({
          interrupt_reply: { interrupt_id: interruptId, answers },
        });
        if (cancelledRef.current) return;
        const settled: IRainInterruptState = {
          ...pending,
          isSubmitting: false,
          answered: true,
          answers,
        };
        ownerSink.interrupts.set(interruptId, settled);
        // Freeze the question card in place — its own re-hydrated, highlighted
        // state is the answer record. Idempotent if the server also emits an
        // interrupt_answer event.
        ownerSink.upsertMessage(buildInterruptMessage(settled));
      } catch (error) {
        if (!cancelledRef.current) {
          const reverted: IRainInterruptState = {
            ...pending,
            isSubmitting: false,
          };
          ownerSink.interrupts.set(interruptId, reverted);
          ownerSink.upsertMessage(buildInterruptMessage(reverted));
        }
        toast.error(t('rain.error.sendMessage'));
        captureException(error as Error, {
          tags: { module: 'rain-chat', action: 'submit_interrupt_reply' },
        });
      } finally {
        if (!cancelledRef.current) setIsSending(false);
      }
    },
    [
      buildInterruptMessage,
      closeOpenFollowUps,
      rootSink,
      sendMessageToAdapter,
      t,
    ],
  );

  // eslint-disable-next-line no-restricted-syntax -- reason: bridge mutable ref so JSX customContent captured in older messages still routes through the latest mutation closure
  useEffect(() => {
    submitInterruptReplyRef.current = submitInterruptReply;
  }, [submitInterruptReply]);

  const resolveRequestConnection = useCallback(
    async (toolCallId: string, connectionId: string) => {
      const owner = findToolCallOwner(toolCallId);
      if (!owner) return;
      const { sink, state } = owner;
      // Submit the result once. isSubmitting guards the window before Rain
      // echoes the tool_result so a re-render (or a competing
      // failRequestConnection) can't post a second result for the same id.
      if (state.status !== 'running' || state.isSubmitting) return;

      const pending: IRainToolCallState = {
        ...state,
        isSubmitting: true,
        resolvedConnectionId: connectionId,
      };
      sink.toolCalls.set(toolCallId, pending);
      sink.upsertMessage(buildToolCallMessage(pending));

      try {
        await sendToolResultToAdapter({
          tool_call_id: toolCallId,
          content: { string_value: connectionId },
          success: true,
        });
        // Leave isSubmitting:true until Rain echoes a tool_result event back —
        // applyToolResultToSink will flip the card to status:'success'.
      } catch (error) {
        if (!cancelledRef.current) {
          const reverted: IRainToolCallState = {
            ...pending,
            isSubmitting: false,
          };
          sink.toolCalls.set(toolCallId, reverted);
          sink.upsertMessage(buildToolCallMessage(reverted));
        }
        toast.error(t('rain.error.sendMessage'));
        captureException(error as Error, {
          tags: { module: 'rain-chat', action: 'resolve_request_connection' },
        });
      }
    },
    [buildToolCallMessage, findToolCallOwner, sendToolResultToAdapter, t],
  );

  // eslint-disable-next-line no-restricted-syntax -- reason: bridge mutable ref so JSX customContent captured in older messages still routes through the latest mutation closure
  useEffect(() => {
    resolveRequestConnectionRef.current = resolveRequestConnection;
  }, [resolveRequestConnection]);

  const failRequestConnection = useCallback(
    async (toolCallId: string) => {
      // Same sink-locator pattern as resolveRequestConnection: the tool call
      // may live on the root sink or inside a sub-agent task sink.
      const owner = findToolCallOwner(toolCallId);
      if (!owner) return;
      const { sink, state } = owner;
      // Report the failure once. After Rain echoes the tool_result the status
      // flips to 'error'; isSubmitting guards the window before that echo so a
      // re-render of the connection widget can't double-send.
      if (state.status !== 'running' || state.isSubmitting) return;

      const appName =
        typeof state.args.appName === 'string' ? state.args.appName : '';
      const appVersion =
        typeof state.args.appVersion === 'string' ? state.args.appVersion : '';
      // Agent-facing message (not shown in the UI), kept as plain English so
      // the agent can reason about it and retry with a valid integration.
      // Names only what was actually asked for: with `appVersion` optional, a
      // phantom `version ""` reads as "your version was wrong" and invites a
      // retry with an invented one. Points at the catalog-search tool, since
      // the usual cause is a display name where a book name was needed
      // ("Intelligent Document Processing" vs. `idp`) — the agent can't fix
      // that by guessing again.
      const requested = appVersion
        ? `"${appName}" version "${appVersion}"`
        : `"${appName}"`;
      // Matched as a bare literal: `request_connection` is declared by each
      // agent that offers it, so there is no shared enum to compare against.
      // smith reaches the same card under `add_integration` and takes the
      // generic branch, as it did before this split.
      const discovery =
        state.name === 'request_connection'
          ? ' Use search_apps to look up the correct integration name, then request the connection again.'
          : ' Search the integration catalog for the correct name, then request the connection again.';
      const error = `No integration named ${requested} is available in this workspace.${discovery}`;

      const pending: IRainToolCallState = { ...state, isSubmitting: true };
      sink.toolCalls.set(toolCallId, pending);
      sink.upsertMessage(buildToolCallMessage(pending));

      try {
        await sendToolResultToAdapter({
          tool_call_id: toolCallId,
          success: false,
          error,
        });
        // Leave isSubmitting:true until Rain echoes a tool_result event back —
        // applyToolResultToSink will flip the card to status:'error'.
      } catch (err) {
        if (!cancelledRef.current) {
          const reverted: IRainToolCallState = {
            ...pending,
            isSubmitting: false,
          };
          sink.toolCalls.set(toolCallId, reverted);
          sink.upsertMessage(buildToolCallMessage(reverted));
        }
        captureException(err as Error, {
          tags: { module: 'rain-chat', action: 'fail_request_connection' },
        });
      }
    },
    [buildToolCallMessage, findToolCallOwner, sendToolResultToAdapter],
  );

  // eslint-disable-next-line no-restricted-syntax -- reason: bridge mutable ref so JSX customContent captured in older messages still routes through the latest mutation closure
  useEffect(() => {
    failRequestConnectionRef.current = failRequestConnection;
  }, [failRequestConnection]);

  /**
   * Skip a pending connection request. Deliberately posts NO tool result: Rain
   * cancels a suspended remote call when user input arrives, appending the
   * cancellation before the message under the generation lock (see
   * `redirect_recovery.rs`). Posting one here too would race that append and
   * duplicate the result for a single tool_call_id.
   */
  const skipRequestConnection = useCallback(
    async (toolCallId: string) => {
      const owner = findToolCallOwner(toolCallId);
      if (!owner) return;
      const { sink, state } = owner;
      // Same guard as the resolve/fail paths: an answered call can't be skipped,
      // and isSubmitting closes the window before Rain's echo lands so a
      // double-click can't send the reply twice.
      if (state.status !== 'running' || state.isSubmitting) return;

      const appName =
        typeof state.args.appName === 'string' ? state.args.appName : '';

      const pending: IRainToolCallState = { ...state, isSubmitting: true };
      sink.toolCalls.set(toolCallId, pending);
      sink.upsertMessage(buildToolCallMessage(pending));

      const sent = await sendMessageToThread({
        content: skipConnectionMessage(appName),
      });
      if (cancelledRef.current) return;

      // The reply is on its way: leave isSubmitting set and let the echoed
      // cancellation flip the card to 'error' (its terminal, read-only state).
      // A rejected send leaves the request actionable so the user can retry —
      // sendMessageToThread has already surfaced the error toast.
      if (!sent) {
        const reverted: IRainToolCallState = {
          ...pending,
          isSubmitting: false,
        };
        sink.toolCalls.set(toolCallId, reverted);
        sink.upsertMessage(buildToolCallMessage(reverted));
      }
    },
    [buildToolCallMessage, findToolCallOwner, sendMessageToThread],
  );

  // eslint-disable-next-line no-restricted-syntax -- reason: bridge mutable ref so JSX customContent captured in older messages still routes through the latest mutation closure
  useEffect(() => {
    skipRequestConnectionRef.current = skipRequestConnection;
  }, [skipRequestConnection]);

  // Resolve the Agent Builder's `attach_file` card: register the assets the
  // user picked/uploaded as Knowledge Files on the target agent (via the
  // scope-bound `registerKnowledgeFiles` handler), then post the attached file
  // names back as the tool result so the paused agent loop resumes. Same
  // sink-locator + isSubmitting guard as resolveRequestConnection.
  const resolveAttachFile = useCallback(
    async (toolCallId: string, assets: V1Asset[]) => {
      const owner = findToolCallOwner(toolCallId);
      if (!owner) return;
      const { sink, state } = owner;
      if (state.status !== 'running' || state.isSubmitting) return;

      const register = agentConfig.registerKnowledgeFiles;
      if (!register) return;

      const pending: IRainToolCallState = { ...state, isSubmitting: true };
      sink.toolCalls.set(toolCallId, pending);
      sink.upsertMessage(buildToolCallMessage(pending));

      try {
        const names = await register(
          assets
            .filter(a => a.reference_uri)
            .map(a => ({
              display_name: a.display_name,
              reference_uri: a.reference_uri ?? '',
            })),
        );
        await sendToolResultToAdapter({
          tool_call_id: toolCallId,
          content: { string_value: names.join(', ') },
          success: true,
        });
        // Leave isSubmitting:true until Rain echoes a tool_result event back —
        // applyToolResultToSink flips the card to status:'success'.
      } catch (error) {
        if (!cancelledRef.current) {
          const reverted: IRainToolCallState = {
            ...pending,
            isSubmitting: false,
          };
          sink.toolCalls.set(toolCallId, reverted);
          sink.upsertMessage(buildToolCallMessage(reverted));
        }
        toast.error(t('rain.error.sendMessage'));
        captureException(error as Error, {
          tags: { module: 'rain-chat', action: 'resolve_attach_file' },
        });
      }
    },
    [
      agentConfig,
      buildToolCallMessage,
      findToolCallOwner,
      sendToolResultToAdapter,
      t,
    ],
  );

  // eslint-disable-next-line no-restricted-syntax -- reason: bridge mutable ref so JSX customContent captured in older messages still routes through the latest mutation closure
  useEffect(() => {
    resolveAttachFileRef.current = resolveAttachFile;
  }, [resolveAttachFile]);

  // Upload variant of resolveAttachFile: the picker's Upload tab hands over raw
  // File objects; upload+register them via the scope-bound
  // `uploadKnowledgeFiles`, then post the attached names back. Same guards.
  const resolveAttachFileUpload = useCallback(
    async (toolCallId: string, files: File[]) => {
      const owner = findToolCallOwner(toolCallId);
      if (!owner) return;
      const { sink, state } = owner;
      if (state.status !== 'running' || state.isSubmitting) return;

      const upload = agentConfig.uploadKnowledgeFiles;
      if (!upload) return;

      const pending: IRainToolCallState = { ...state, isSubmitting: true };
      sink.toolCalls.set(toolCallId, pending);
      sink.upsertMessage(buildToolCallMessage(pending));

      try {
        const names = await upload(files);
        await sendToolResultToAdapter({
          tool_call_id: toolCallId,
          content: { string_value: names.join(', ') },
          success: true,
        });
      } catch (error) {
        if (!cancelledRef.current) {
          const reverted: IRainToolCallState = {
            ...pending,
            isSubmitting: false,
          };
          sink.toolCalls.set(toolCallId, reverted);
          sink.upsertMessage(buildToolCallMessage(reverted));
        }
        toast.error(t('rain.error.sendMessage'));
        captureException(error as Error, {
          tags: { module: 'rain-chat', action: 'resolve_attach_file_upload' },
        });
      }
    },
    [
      agentConfig,
      buildToolCallMessage,
      findToolCallOwner,
      sendToolResultToAdapter,
      t,
    ],
  );

  // eslint-disable-next-line no-restricted-syntax -- reason: bridge mutable ref so JSX customContent captured in older messages still routes through the latest mutation closure
  useEffect(() => {
    resolveAttachFileUploadRef.current = resolveAttachFileUpload;
  }, [resolveAttachFileUpload]);

  // The user dismissed the file picker without attaching anything — report a
  // benign failure so the agent turn resumes and the agent can move on. Same
  // sink-locator + isSubmitting guard as failRequestConnection.
  const failAttachFile = useCallback(
    async (toolCallId: string) => {
      const owner = findToolCallOwner(toolCallId);
      if (!owner) return;
      const { sink, state } = owner;
      if (state.status !== 'running' || state.isSubmitting) return;

      // Agent-facing message (not shown in the UI) so the agent knows the user
      // opted not to attach a file and can continue rather than wait.
      const error =
        'The user closed the file picker without attaching any files.';

      const pending: IRainToolCallState = { ...state, isSubmitting: true };
      sink.toolCalls.set(toolCallId, pending);
      sink.upsertMessage(buildToolCallMessage(pending));

      try {
        await sendToolResultToAdapter({
          tool_call_id: toolCallId,
          success: false,
          error,
        });
        // Leave isSubmitting:true until Rain echoes a tool_result event back —
        // applyToolResultToSink flips the card to status:'error'.
      } catch (err) {
        if (!cancelledRef.current) {
          const reverted: IRainToolCallState = {
            ...pending,
            isSubmitting: false,
          };
          sink.toolCalls.set(toolCallId, reverted);
          sink.upsertMessage(buildToolCallMessage(reverted));
        }
        captureException(err as Error, {
          tags: { module: 'rain-chat', action: 'fail_attach_file' },
        });
      }
    },
    [buildToolCallMessage, findToolCallOwner, sendToolResultToAdapter],
  );

  // eslint-disable-next-line no-restricted-syntax -- reason: bridge mutable ref so JSX customContent captured in older messages still routes through the latest mutation closure
  useEffect(() => {
    failAttachFileRef.current = failAttachFile;
  }, [failAttachFile]);

  // Resolve a remote tool with a value the card collected, such as astral's
  // `request_review` dialog handing back `{ fieldName: finalValue }`. Encoded as
  // the tool's result `content` to resume generation. Returns true on success so
  // the dialog can close itself (mirrors the astral save's boolean contract).
  const resolveWithValue = useCallback(
    async (toolCallId: string, content: Rainv1Value): Promise<boolean> => {
      // Same sink-locator pattern as resolveRequestConnection — the call may
      // live on the root sink or inside a sub-agent task sink.
      const owner = findToolCallOwner(toolCallId);
      if (!owner) return false;
      const { sink, state } = owner;
      // Submit once; isSubmitting guards the window before Rain echoes the
      // tool_result so a re-open of the dialog can't double-send.
      if (state.status !== 'running' || state.isSubmitting) return false;

      const pending: IRainToolCallState = { ...state, isSubmitting: true };
      sink.toolCalls.set(toolCallId, pending);
      sink.upsertMessage(buildToolCallMessage(pending));

      try {
        await sendToolResultToAdapter({
          tool_call_id: toolCallId,
          content,
          success: true,
        });
        // Leave isSubmitting:true until Rain echoes a tool_result event back —
        // applyToolResultToSink will flip the card to status:'success'.
        return true;
      } catch (error) {
        if (!cancelledRef.current) {
          const reverted: IRainToolCallState = {
            ...pending,
            isSubmitting: false,
          };
          sink.toolCalls.set(toolCallId, reverted);
          sink.upsertMessage(buildToolCallMessage(reverted));
        }
        toast.error(t('rain.error.sendMessage'));
        captureException(error as Error, {
          tags: {
            module: 'rain-chat',
            action: 'resolve_with_value',
            tool: pending.name,
          },
        });
        return false;
      }
    },
    [buildToolCallMessage, findToolCallOwner, sendToolResultToAdapter, t],
  );

  // eslint-disable-next-line no-restricted-syntax -- reason: bridge mutable ref so JSX customContent captured in older messages still routes through the latest mutation closure
  useEffect(() => {
    resolveWithValueRef.current = resolveWithValue;
  }, [resolveWithValue]);

  // Resolves a remote tool blocked on an approve/reject decision, such as
  // astral's `request_guide_approval`. Sends only the decision as the
  // tool_result so the agent turn unblocks; any side effect of deciding (the
  // guide entry's APPROVE/REJECT transition, via useGuideEntryTransition) is the
  // card's own. Same sink-locator + isSubmitting guard as resolveRequestConnection.
  const resolveWithDecision = useCallback(
    async (toolCallId: string, approved: boolean): Promise<boolean> => {
      const owner = findToolCallOwner(toolCallId);
      if (!owner) return false;
      const { sink, state } = owner;
      if (state.status !== 'running' || state.isSubmitting) return false;

      const pending: IRainToolCallState = { ...state, isSubmitting: true };
      sink.toolCalls.set(toolCallId, pending);
      sink.upsertMessage(buildToolCallMessage(pending));

      try {
        await sendToolResultToAdapter({
          tool_call_id: toolCallId,
          content: { bool_value: approved },
          success: true,
        });
        // Leave isSubmitting:true until Rain echoes a tool_result event back —
        // applyToolResultToSink will flip the card to status:'success'.
        return true;
      } catch (error) {
        if (!cancelledRef.current) {
          const reverted: IRainToolCallState = {
            ...pending,
            isSubmitting: false,
          };
          sink.toolCalls.set(toolCallId, reverted);
          sink.upsertMessage(buildToolCallMessage(reverted));
        }
        toast.error(t('rain.error.sendMessage'));
        captureException(error as Error, {
          tags: {
            module: 'rain-chat',
            action: 'resolve_with_decision',
            tool: pending.name,
          },
        });
        return false;
      }
    },
    [buildToolCallMessage, findToolCallOwner, sendToolResultToAdapter, t],
  );

  // eslint-disable-next-line no-restricted-syntax -- reason: bridge mutable ref so JSX customContent captured in older messages still routes through the latest mutation closure
  useEffect(() => {
    resolveWithDecisionRef.current = resolveWithDecision;
  }, [resolveWithDecision]);

  const submitToolApproval = useCallback(
    async (toolCallId: string, approved: boolean, reason?: string) => {
      // Same sink-locator pattern as resolveRequestConnection: the tool call
      // may live on the root sink or inside a sub-agent task sink.
      const owner = findToolCallOwner(toolCallId);
      if (!owner) {
        captureMessage(
          'Rain tool approval reply targeted an unknown tool call',
          {
            level: 'warning',
            tags: { module: 'rain-chat', action: 'submit_tool_approval' },
            extra: { toolCallId, approved },
          },
        );
        return;
      }
      const { sink, state } = owner;

      // The user replying ends the awaiting-approval pause and starts a new
      // generation turn — close any open follow-up card so the next batch
      // opens a fresh one.
      closeOpenFollowUps();

      const pending: IRainToolCallState = { ...state, isSubmitting: true };
      sink.toolCalls.set(toolCallId, pending);
      sink.upsertMessage(buildToolCallMessage(pending));
      setIsSending(true);
      // Optimistically resume the turn — the server's tool_approval_granted/
      // _denied echo would also drive this, but it can arrive after the API
      // resolves so we'd otherwise drop the Stop button between.
      dispatchGeneration({ type: 'start-turn' });

      try {
        await sendMessageToAdapter({
          tool_approval_reply: {
            tool_call_id: toolCallId,
            approved,
            ...(reason !== undefined ? { reason } : {}),
          },
        });
        // Leave isSubmitting:true; the matching tool_approval_granted or
        // tool_approval_denied event from the stream clears it.
      } catch (error) {
        if (!cancelledRef.current) {
          const reverted: IRainToolCallState = {
            ...pending,
            isSubmitting: false,
          };
          sink.toolCalls.set(toolCallId, reverted);
          sink.upsertMessage(buildToolCallMessage(reverted));
        }
        toast.error(t('rain.error.sendMessage'));
        captureException(error as Error, {
          tags: { module: 'rain-chat', action: 'submit_tool_approval' },
        });
      } finally {
        if (!cancelledRef.current) setIsSending(false);
      }
    },
    [
      buildToolCallMessage,
      closeOpenFollowUps,
      findToolCallOwner,
      sendMessageToAdapter,
      t,
    ],
  );

  // eslint-disable-next-line no-restricted-syntax -- reason: bridge mutable ref so JSX customContent captured in older messages still routes through the latest mutation closure
  useEffect(() => {
    submitToolApprovalRef.current = submitToolApproval;
  }, [submitToolApproval]);

  const sendFollowUp = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;
      await sendMessageToThread({ content: trimmed });
    },
    [sendMessageToThread],
  );

  // eslint-disable-next-line no-restricted-syntax -- reason: bridge mutable ref so JSX customContent captured in older messages still routes through the latest closure
  useEffect(() => {
    sendFollowUpRef.current = sendFollowUp;
  }, [sendFollowUp]);

  // Resolves a max-iterations question card: freezes it as answered and sends
  // the matching instruction back to the agent. Continuing re-arms the backend
  // tool loop for another batch; breaking down hands control back to the user.
  // Both routes go through the normal send path so the reply is echoed as a
  // regular outgoing message and a fresh turn opens (Stop button, working
  // indicator, etc.).
  const resolveMaxIterations = useCallback(
    async (id: string, shouldContinue: boolean) => {
      const existing = rootMaxIterationsRef.current.get(id);
      // Guard the window before the reply's own turn begins so a double-click
      // (or a re-render of the card) can't submit the choice twice.
      if (!existing || existing.answered || existing.isSubmitting) return;

      const pending: IRainMaxIterationsState = {
        ...existing,
        isSubmitting: true,
        choseContinue: shouldContinue,
      };
      rootMaxIterationsRef.current.set(id, pending);
      rootSink.upsertMessage(buildMaxIterationsMessage(pending));

      const content = shouldContinue
        ? MAX_ITERATIONS_CONTINUE_MESSAGE
        : MAX_ITERATIONS_STOP_MESSAGE;
      const sent = await sendMessageToThread({ content });
      if (cancelledRef.current) return;

      // Freeze the card read-only once the reply is on its way (or revert to
      // actionable if the send was rejected so the user can retry).
      const settled: IRainMaxIterationsState = {
        ...pending,
        isSubmitting: false,
        answered: sent,
      };
      rootMaxIterationsRef.current.set(id, settled);
      rootSink.upsertMessage(buildMaxIterationsMessage(settled));
    },
    [buildMaxIterationsMessage, rootSink, sendMessageToThread],
  );

  // eslint-disable-next-line no-restricted-syntax -- reason: bridge mutable ref so JSX customContent captured in older messages still routes through the latest closure
  useEffect(() => {
    resolveMaxIterationsRef.current = resolveMaxIterations;
  }, [resolveMaxIterations]);

  // Optimistic display of a draft's first message. NewDraftChat already SENT it
  // to this thread (send-at-source) and stashed it for instant render; we
  // consume that stash exactly once and seed the optimistic bubble — we do NOT
  // send here. The server echo (REST backlog or SSE) reconciles it via
  // clearOptimisticMessages. Consume-once at the store guarantees a remount or
  // reload can't replay it; the ref just tames a StrictMode double-invoke.
  // Draw a handed-over first message straight away, without waiting for a
  // thread.
  //
  // Once per MOUNT, not once per hand-off. Those differ, and both halves
  // matter: this component is keyed on the thread id, so it remounts the
  // moment the thread arrives and has to re-seed or the message blinks out of
  // the transcript — but within a single mount, seeding twice puts the user's
  // message on screen twice. The store object is replaced when the send claims
  // it (`isDispatched` flips), and without this ref that new identity re-ran
  // the effect and drew a second bubble.
  //
  // A LAYOUT effect, so the seed is applied before the browser paints. In a
  // plain effect the new mount painted its empty transcript first and the
  // message and thinking row visibly flashed on the hand-over.
  const draftSeedAppliedRef = useRef(false);

  // eslint-disable-next-line no-restricted-syntax -- reason: must run before paint so the remount never shows an empty transcript; guarded once-per-mount
  useLayoutEffect(() => {
    if (!pendingFirstMessage) return;
    if (draftSeedAppliedRef.current) return;
    draftSeedAppliedRef.current = true;
    seedOptimisticOutgoing(
      pendingFirstMessage.content ?? '',
      pendingFirstMessage.attachments ?? [],
    );
    // Show the working state under it, so the wait for the thread reads as the
    // agent thinking rather than as nothing happening.
    dispatchGeneration({ type: 'start-turn' });
  }, [pendingFirstMessage, seedOptimisticOutgoing]);

  // eslint-disable-next-line no-restricted-syntax -- reason: One-shot consume of the pending first-message stash to seed an optimistic bubble
  useEffect(() => {
    // Wait for the thread rather than consuming nothing and latching. The chat
    // now mounts before its thread exists, so this effect runs at least once
    // with no id; marking it consumed then would drop the stashed message for
    // good and the user's own first message would never appear.
    if (!rainThreadId) return;
    if (pendingSeedConsumedRef.current) return;
    pendingSeedConsumedRef.current = true;
    const pending = useRainPendingFirstMessageStore
      .getState()
      .consumePendingRainMessage(rainThreadId);
    if (!pending) return;
    seedOptimisticOutgoing(pending.content, pending.attachments);
    // Show the working state immediately, before the input echo / SSE lands.
    dispatchGeneration({ type: 'start-turn' });
  }, [rainThreadId, seedOptimisticOutgoing]);

  // Undoes the seed above when the caller that owns the send is told it failed.
  // Same two steps `sendMessageToThread` takes on a rejected send — drop the
  // placeholder, end the turn — except the send happened outside this hook, so
  // only that caller can trigger them. Not `stopGeneration`: there is no server
  // turn to stop, and its own settle only runs once `adapter.stop()` resolves.
  const abandonSeededTurn = useCallback(() => {
    clearOptimisticMessages();
    dispatchGeneration({ type: 'settle-turn' });
  }, [clearOptimisticMessages]);

  // Stops the current generation turn. Wraps the scope-bound adapter mutation
  // with shared error handling so every chat surface (editor + run page) stops
  // the same way.
  const stopGeneration = useCallback(async () => {
    try {
      await adapter.stop();
      /*
       * Settle the turn ourselves. The reducer clears `hasActiveTurn` on a
       * server `generation_complete`, and a stopped turn may never emit one —
       * leaving `isGenerating` true, which is what `RainChatView` disables the
       * composer on. The operator would have stopped the agent and then been
       * unable to say anything at all.
       */
      dispatchGeneration({ type: 'settle-turn' });
    } catch (error) {
      toast.error(t('rain.error.stopThread'));
      captureException(error as Error, {
        tags: { module: 'rain-chat', action: 'stop_generation' },
      });
    }
    // adapter.stop is useCallback-stable per scope; depending on it directly
    // keeps this callback stable across renders. `dispatchGeneration` is a
    // useReducer dispatch, so it is stable by definition.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adapter.stop, t]);

  // Wakes the thread from a pending sleep (the sleep card's "Wake up"
  // button). The actual state flip (status -> 'completed',
  // interruptedByUser -> true) comes from the `sleep_completed` stream event
  // this triggers server-side, same as when the user sends a message during
  // a sleep — this callback only fires the request and reports failures.
  const wakeThread = useCallback(async () => {
    try {
      await adapter.wake();
    } catch (error) {
      toast.error(t('rain.error.wakeThread'));
      captureException(error as Error, {
        tags: { module: 'rain-chat', action: 'wake_thread' },
      });
    }
    // adapter.wake is useCallback-stable per scope; depending on it directly
    // keeps this callback stable across renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adapter.wake, t]);

  // eslint-disable-next-line no-restricted-syntax -- reason: bridge mutable ref so JSX customContent captured in older messages still routes through the latest closure
  useEffect(() => {
    wakeThreadRef.current = wakeThread;
  }, [wakeThread]);

  /**
   * Injects a deterministic tool call into the thread, as though the agent had
   * chosen to call it. Resolves with the server-minted `tool_call_id` so the
   * caller can correlate it against the resulting stream events.
   *
   * Only scopes whose adapter provides `sendToolCall` can do this (today the
   * automation scope, for the editor's Test button); elsewhere it resolves
   * undefined.
   */
  const sendToolCall = useCallback(
    async (
      toolName: string,
      args: Record<string, Rainv1Value>,
    ): Promise<string | undefined> => {
      const response = await adapter.sendToolCall?.({
        tool_name: toolName,
        arguments: args,
      });
      return response?.tool_call_id;
    },
    [adapter],
  );

  // Each message records the span of event ids it was built from, so a plan's
  // seam is the first message that originated after its `plan_create`.
  const messagesWithPlans = useMemo(() => {
    const streamPlans = selectStreamPlans(planStreamRef.current);
    if (streamPlans.length === 0) return messages;

    const origins = messageOriginsRef.current;
    // This memo re-runs per streamed token; without a cache every settled card
    // re-renders. `lastEventId` moves whenever the plan's content does.
    const planCard = (entry: (typeof streamPlans)[number]): IChatMessage => {
      const cached = planCardCacheRef.current.get(entry.id);
      if (cached?.lastEventId === entry.lastEventId) return cached.message;
      const message = buildCustomMessage(
        entry.id,
        <RainStreamPlanCard plan={entry.plan} />,
      );
      planCardCacheRef.current.set(entry.id, {
        lastEventId: entry.lastEventId,
        message,
      });
      return message;
    };

    const result: IChatMessage[] = [];
    let next = 0;
    for (const message of messages) {
      // No origin means optimistic, so it sorts after anything in the log.
      const boundary =
        origins.get(message.id)?.first ?? Number.MAX_SAFE_INTEGER;
      while (
        next < streamPlans.length &&
        streamPlans[next].createdAtEventId < boundary
      ) {
        result.push(planCard(streamPlans[next]));
        next += 1;
      }
      result.push(message);
    }
    for (; next < streamPlans.length; next += 1) {
      result.push(planCard(streamPlans[next]));
    }
    return result;
    // planStreamVersion stands in for the mutable ref the plans live in.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, planStreamVersion]);

  // Same log as the transcript cards, so the two can never both claim a plan.
  const livePlanRetired = useMemo(
    () => isLivePlanRetired(planStreamRef.current),
    // planStreamVersion stands in for the mutable ref the plans live in.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [planStreamVersion],
  );

  return {
    messages: messagesWithPlans,
    isLivePlanRetired: livePlanRetired,
    sendMessageToThread,
    submitInterruptReply,
    submitToolApproval,
    resolveRequestConnection,
    failRequestConnection,
    skipRequestConnection,
    resolveAttachFile,
    failAttachFile,
    resolveWithValue,
    resolveWithDecision,
    stopGeneration,
    abandonSeededTurn,
    sendToolCall,
    rebuildToolCards,
    isSending,
    // Derived from the generation-state reducer (handles streaming, pauses
    // for interrupts/approvals/sleeps, in-flight compactions, retryable
    // failure parking, multi-turn loops). `isSending` is OR'd in to cover
    // the optimistic window between a user send and the server's input echo.
    isGenerating: isSending || selectIsGenerating(generationState),
    isRunningInternalTool,
    isLoadingHistory: !backlogDone && !backlogError,
    backlogError: backlogError as Error | null,
    renderWorkBlock: agentConfig.renderWorkBlock,
    filterUserMessages: shouldFilterUserMessages(
      agentConfig.filterUserMessages,
      adapter.agentVersion,
    ),
    activeAgentId,
  };
}
